import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Loader } from "components/Loader";
import { checkFirstLevelApproverPermission } from "services/utils/HelperFunction";
import {
  checkViewerPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
import { OtReports } from "./OtReports";
import {
  punchDetailsHeader,
  workingHoursHeader,
  workingHoursHeaders,
} from "../ReportHeaders";
import { tableRowSx } from "services/constants";
import {
  DriveFileRenameOutline,
  Groups2Rounded,
  EventAvailableRounded,
  EventBusyRounded,
  BeachAccessRounded,
  ScheduleRounded,
  CelebrationRounded,
  WeekendRounded,
  MoreTimeRounded,
  FreeBreakfastRounded,
  SearchRounded,
  Badge,
  WatchLaterRounded,
  AccessTimeRounded,
} from "@mui/icons-material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import moment from "moment";
import * as Mui from "@mui/material";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import {
  themes,
  reports,
  defaultAllSelection,
  punchInDetails,
  optionalAvathar,
  adminRoleName,
  supportLabel,
  theme,
} from "services/constants";
import { showToast } from "services/utils/Status";
import {
  getAdjustmentModifiedAt,
  getAdjustmentModifiedBy,
  isAdjustmentPunchRow,
  PunchDetails,
} from "./PunchDetails";
import {
  setDepartmentId,
  setDepartmentName,
  setFilterApi,
} from "services/Redux/userManagament";
import sessionHandling from "services/utils/notificationUtils";
import { attendanceStatusHalfBox } from "services/constants/Styles";
import { EditCheckInOut } from "pages/TeamZone/Attendance/AttendanceStatus/Status/EditCheckInOut";
import { tooltipStyleProps } from "services/constants/Styles";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import {
  TableEmptyState,
  EmptyState,
} from "components/CustomComponents/EmptyState";
import { FilterProvider } from "pages/FilterContext";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import { useFetch } from "services/hooks/useFetch";
import { WrkedHrsReports } from "../WorkingHoursReport";
import { TimeCardReport } from "./TimeCardReport";
import { RawPunchEntryDetailsReport } from "../RawPunchEntryDetails";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import {
  APP_BAR_OFFSET,
  compactRowPager,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const SUMMARIZED_WORKING_HOURS_TYPE = "Summarized working hours";
const DETAILED_WORKING_HOURS_TYPE = "Detailed working hours";
const PUNCH_DETAILS_TYPE = "Punch Entry";
const BREAK_REPORT_TYPE = "Break";
const ADJUSTMENT_REPORT_TYPE = "Adjustment Entry";
const WORKING_HOURS_REPORT_TYPE = "Working Hours";
const TIME_CARD_REPORT_TYPE = "Time card";
const RAW_PUNCH_ENTRY_TYPE = "Raw Punch Entry";
const DETAILED_WORKING_HOURS_SAVED_VIEW_KEY =
  "detailed_working_hours_saved_views";
const BREAK_REPORT_SAVED_VIEW_KEY = "break_report_saved_views";
// Fixed page size for a single expanded date-wise row's employee list —
// deliberately not user-selectable, unlike the outer table's rowsPerPage.
const BREAK_DATE_DETAIL_PAGE_SIZE = 25;
// Date-wise outer tables (Break, Punch Entry, Adjustment Entry) are bounded
// to a date range's worth of rows rather than a whole employee roster, so a
// smaller page size than the app-wide default reads better.
const DATE_WISE_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const WORKING_HOURS_DEFAULT_VIEW_ID = "__teamzone_working_hours_default_view__";
const BREAK_EXPAND_OPTIONS = [
  { id: "collapse-all", label: "Collapse All" },
  { id: "expand-all", label: "Expand All" },
];
// Bounded the same way as Break's — a date range is naturally small, and
// each expand only pages the currently visible outer page's dates (server
// pagination per date, not everything at once), so there's no repeat of
// the thousands-of-employees hang Expand All caused before.
const DETAILED_DATE_EXPAND_OPTIONS = [
  { id: "collapse-all", label: "Collapse All" },
  { id: "expand-all", label: "Expand All" },
];
const BREAK_VIEW_MODE_OPTIONS = [
  { id: "employee-wise", label: "Employee Wise" },
  { id: "date-wise", label: "Date Wise" },
];
const PUNCH_VIEW_MODE_OPTIONS = [
  { id: "employee-wise", label: "Employee Wise" },
  { id: "date-wise", label: "Date Wise" },
];
const TIME_CARD_VIEW_MODE_OPTIONS = [
  { id: "employee-wise", label: "Employee Wise" },
  { id: "date-wise", label: "Date Wise" },
];
// Detailed Working Hours' Date Wise option has no data behind it yet — its
// employee-day breakdown is fetched lazily per employee (see
// getCheckInCheckOutDetails below), unlike Break/Punch/Time Card which load
// every employee's full day-by-day array upfront and can regroup it by date
// client-side. Date Wise needs a genuinely new, date-first API; until that
// exists this option renders an empty/coming-soon state.
const DETAILED_VIEW_MODE_OPTIONS = [
  { id: "employee-wise", label: "Employee Wise" },
  { id: "date-wise", label: "Date Wise" },
];

const formatWorkingHoursHeaderName = (key, fallbackName) => {
  switch (key) {
    case "employee_name":
      return "Name";
    case "employee_id":
      return "Employee ID";
    case "total_working_hours":
      return "Working Hrs";
    case "shift_working_hours":
    case "shift_hours":
      return "Shift Hrs";
    case "ot_hours":
    case "over_time":
      return "OT Hrs";
    case "total_break_hours":
      return "Break Hrs";
    case "working_hours":
      return "Working Hrs";
    case "leave_credit_hours":
      return "Leave Credit Hrs";
    default:
      return fallbackName;
  }
};

// Per-column pixel widths so the table can size itself naturally instead
// of squeezing under table-layout:"fixed" — mirrors the sibling
// WorkingHoursReport/WorkingHours.js's getEmployeeColumnWidth. These sum
// into the table's own minWidth (see workingHoursSummaryTableSx below): on
// a screen too narrow for that sum, TableContainer's overflowX:"auto"
// gives the table its own contained horizontal scrollbar so every column
// keeps this exact width (no compression, no mid-word breaks); on a wider
// screen the table just renders at this natural width with room to spare.
const WORKING_HOURS_COLUMN_WIDTH = {
  employee_name: { xs: 220, lg: 190, xl: 220 },
  employee_id: { xs: 120, lg: 110, xl: 120 },
  department_name: { xs: 160, lg: 140, xl: 160 },
  defaultString: { xs: 150, lg: 120, xl: 150 },
  time: { xs: 110, lg: 92, xl: 110 },
  default: { xs: 100, lg: 88, xl: 100 },
};

const getWorkingHoursColumnWidth = (header, breakpoint = "xs") => {
  const keyWidth = WORKING_HOURS_COLUMN_WIDTH[header.key];
  if (keyWidth) {
    return keyWidth[breakpoint] || keyWidth.xs;
  }
  if (header.type === "string") {
    return (
      WORKING_HOURS_COLUMN_WIDTH.defaultString[breakpoint] ||
      WORKING_HOURS_COLUMN_WIDTH.defaultString.xs
    );
  }
  if (header.type === "time") {
    return (
      WORKING_HOURS_COLUMN_WIDTH.time[breakpoint] ||
      WORKING_HOURS_COLUMN_WIDTH.time.xs
    );
  }
  return (
    WORKING_HOURS_COLUMN_WIDTH.default[breakpoint] ||
    WORKING_HOURS_COLUMN_WIDTH.default.xs
  );
};

const workingHoursDetailTableContainerSx = {
  marginTop: 0,
  marginLeft: "auto",
  marginRight: "auto",
  position: "relative",
  width: "100%",
  maxHeight: 650,
  overflow: "auto",
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
  // Rows become taller cards below 900px, so drop the fixed height cap.
  "@media (max-width:899.95px)": { maxHeight: "none", overflow: "visible" },
};

const workingHoursDetailTableSx = {
  "& .MuiTableHead-root": {
    ...designShell.tableStickyHead,
    zIndex: 3,
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    ...designShell.tableStickyHeadRow,
    zIndex: 3,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...approveTableHeadCell,
    // approveTableHeadCell's top:APP_BAR_OFFSET (63px) is calibrated for
    // the page-level sticky headers elsewhere in this file — this table
    // is different: it lives inside workingHoursDetailTableContainerSx,
    // which has its own maxHeight:650 + overflow:"auto", making IT the
    // real scrolling ancestor. Sticking 63px down from THIS box's own top
    // (instead of top:0) left a 63px gap where the row above could still
    // peek through above the header as you scrolled — the stray red
    // Status/Action marks and misaligned header the report described.
    top: 0,
    zIndex: 1,
    backgroundColor: designColors.slate100,
  },
  "& .MuiTableBody-root .MuiTableCell-root": approveTableBodyCell,
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

// Break Report's two levels of nested drill-down (per-employee shift days,
// then per-shift break entries) — same local boxed-scroll pattern as
// workingHoursDetailTableContainerSx/Sx above, sized smaller since these
// nest one level deeper.
const breakShiftTableContainerSx = {
  marginTop: 0,
  marginLeft: "auto",
  marginRight: "auto",
  position: "relative",
  width: "100%",
  maxHeight: 650,
  overflow: "auto",
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
  // Rows become taller cards below 900px, so drop the fixed height cap.
  "@media (max-width:899.95px)": { maxHeight: "none", overflow: "visible" },
};

const breakShiftTableSx = {
  "& .MuiTableHead-root": {
    ...designShell.tableStickyHead,
    zIndex: 3,
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    ...designShell.tableStickyHeadRow,
    zIndex: 3,
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...approveTableHeadCell,
    top: 0,
    zIndex: 1,
    backgroundColor: designColors.slate100,
  },
  "& .MuiTableBody-root .MuiTableCell-root": approveTableBodyCell,
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

const breakEntryTableContainerSx = {
  marginTop: 0,
  marginLeft: "auto",
  marginRight: "auto",
  width: "100%",
  border: `1px solid ${designColors.slate200}`,
  borderRadius: 2,
  backgroundColor: designColors.white,
};

const breakEntryTableSx = {
  "& .MuiTableHead-root .MuiTableCell-root": {
    ...approveTableHeadCell,
    position: "static",
    backgroundColor: designColors.slate100,
  },
  "& .MuiTableBody-root .MuiTableCell-root": approveTableBodyCell,
  "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
    borderBottom: "none",
  },
};

const normalizeReportType = (reportType) => {
  switch (reportType) {
    case "Punch details":
      return PUNCH_DETAILS_TYPE;
    case "Adjustment Report":
      return ADJUSTMENT_REPORT_TYPE;
    case "Break Report":
      return BREAK_REPORT_TYPE;
    case "Working Hours Report":
      return WORKING_HOURS_REPORT_TYPE;
    case "Time card Report":
      return TIME_CARD_REPORT_TYPE;
    default:
      return reportType;
  }
};

const isPunchBasedReport = (reportType) =>
  reportType === PUNCH_DETAILS_TYPE || reportType === ADJUSTMENT_REPORT_TYPE;
const sumHhMmValues = (values) => {
  const totalMinutes = (values || []).reduce((sum, value) => {
    const match = /^(\d{1,4}):(\d{2})$/.exec(`${value ?? ""}`.trim());
    if (!match) return sum;
    return sum + Number(match[1]) * 60 + Number(match[2]);
  }, 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

const getDetailedTotalValue = (details, totalKeys, detailKeys) => {
  const apiTotalKey = totalKeys.find(
    (key) => details?.[key] !== undefined && details?.[key] !== null,
  );
  if (apiTotalKey) return details[apiTotalKey] || "00:00";

  const detailRows = Array.isArray(details?.checkin_details)
    ? details.checkin_details
    : [];
  return sumHhMmValues(
    detailRows.map((row) => {
      const detailKey = detailKeys.find(
        (key) => row?.[key] !== undefined && row?.[key] !== null,
      );
      return detailKey ? row[detailKey] : "00:00";
    }),
  );
};

// users_working_hours_user_details (the endpoint Break Report and Time Card
// both fetch from) hands back each employee's day-by-day punch records but
// no present_count/absent_count/etc aggregate on the employee row itself —
// those only exist on the summarized-detailed-workinghours response the
// other reports use. Tally them here from each day's status, the same way
// TimeCardReport.js's own getTimeCardMarker/getStatusDisplay read it.
const getEmployeeDayRecords = (employee) =>
  Array.isArray(employee?.user_details)
    ? employee.user_details
    : Array.isArray(employee?.checkin_details)
      ? employee.checkin_details
      : employee?.days && typeof employee.days === "object"
        ? Object.values(employee.days)
        : [];

const classifyDayStatus = (day) => {
  const rawStatus = `${day?.status || ""}`.trim().toUpperCase();
  const rawLabel = `${
    day?.status_label || day?.type || day?.attendance_status || ""
  }`
    .trim()
    .toLowerCase();

  if (rawStatus === "WO" || rawLabel.includes("week")) return "weeklyOff";
  if (
    rawStatus === "NH" ||
    rawStatus === "FH" ||
    rawStatus === "NHFH" ||
    rawLabel.includes("holiday")
  )
    return "holiday";
  if (rawStatus === "A" || rawLabel.includes("absent")) return "absent";
  if (rawStatus === "P" || rawLabel.includes("present")) return "present";
  return null;
};

// Prefers the API's own aggregate count when it actually sent one (including
// a real zero); only falls back to the day-status tally when the field is
// missing entirely, matching the ?? pattern WorkingHoursReport/index.js
// already uses for the same kind of gap on absent_count.
const resolveDayCount = (rawValue, fallbackCount) => {
  const parsed = Number(rawValue);
  return rawValue !== undefined && rawValue !== null && Number.isFinite(parsed)
    ? parsed
    : fallbackCount;
};

// Every report type defaults to This Month. Types whose picker allows the
// current date (punch-based + raw punch) run to today; the rest exclude today
// (picker maxDate is yesterday), so their default ends yesterday.
const getDefaultDateRange = (reportType) => {
  const includesToday =
    isPunchBasedReport(reportType) || reportType === RAW_PUNCH_ENTRY_TYPE;
  const range = getThisMonthRange({
    endAt: includesToday ? "today" : "yesterday",
  });

  return {
    startApiDate: range.startDate,
    endApiDate: range.endDate,
    displayDateRange: `${moment(range.startDate).format(
      "DD-MM-YYYY",
    )} - ${moment(range.endDate).format("DD-MM-YYYY")}`,
    label: range.label,
  };
};

const normalizeReportFilterDate = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue instanceof Date) {
    const parsed = moment(dateValue);
    return parsed.isValid() ? parsed : null;
  }

  if (moment.isMoment(dateValue)) {
    return dateValue.isValid() ? dateValue : null;
  }

  if (typeof dateValue === "string") {
    const normalizedString = dateValue.includes("/")
      ? dateValue.split("/").reverse().join("-")
      : dateValue;
    const parsed = moment(
      normalizedString,
      ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601],
      true,
    );
    return parsed.isValid() ? parsed : null;
  }

  const fallback = moment(dateValue);
  return fallback.isValid() ? fallback : null;
};

const isCurrentOrFutureDate = (dateValue) => {
  const parsedDate = moment(
    dateValue,
    ["YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY", moment.ISO_8601],
    true,
  );

  return parsedDate.isValid() && !parsedDate.isBefore(moment(), "day");
};

const reportTypeMap = {
  [SUMMARIZED_WORKING_HOURS_TYPE]: "Summarized Working Hours",
  [DETAILED_WORKING_HOURS_TYPE]: "Detailed Working Hours",
  [BREAK_REPORT_TYPE]: BREAK_REPORT_TYPE,
  [ADJUSTMENT_REPORT_TYPE]: ADJUSTMENT_REPORT_TYPE,
  [PUNCH_DETAILS_TYPE]: "Punch Entry",
  [WORKING_HOURS_REPORT_TYPE]: "Working Hours",
  [TIME_CARD_REPORT_TYPE]: "Time card",
};

const breakReportFilterHeaders = [
  {
    name: "Employee Name",
    key: "employee_name",
    type: "string",
    icon: <PersonOutlineIcon fontSize="small" />,
  },
  {
    name: "Employee Id",
    key: "employee_id",
    type: "string",
    icon: <Badge fontSize="small" />,
  },
  {
    name: "Department Name",
    key: "department_name",
    type: "autocomplete",
    icon: <Groups2Rounded fontSize="small" />,
  },
  {
    name: "Total Break Hours",
    key: "total_break_hours",
    type: "time",
    icon: <FreeBreakfastRounded fontSize="small" />,
  },
];

const breakReportDateHeaders = [
  "Date",
  "Shift",
  "Check-In",
  "Check-Out",
  "Break",
];
const breakReportDateWiseHeaders = [
  {
    name: "Date",
    key: "date",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "Break Hours",
    key: "total_break_hours",
    type: "time",
    icon: <FreeBreakfastRounded fontSize="small" />,
  },
];

// Detailed Working Hours' date-wise summary rows — sourced from
// users_working_hours_detailed_summary, one aggregate object per date. No
// "Employees" column, matching the same call on Break/Punch/Adjustment's
// date-wise list views.
const detailedDateWiseHeaders = [
  {
    name: "Date",
    key: "date_key",
    type: "date",
    icon: <ScheduleRounded fontSize="small" />,
  },
  {
    name: "Present",
    key: "total_assigned_present_count",
    type: "number",
    icon: <EventAvailableRounded fontSize="small" />,
  },
  {
    name: "Absent",
    key: "total_assigned_absent_count",
    type: "number",
    icon: <EventBusyRounded fontSize="small" />,
  },
  {
    name: "Leave",
    key: "total_assigned_leave_count",
    type: "number",
    icon: <BeachAccessRounded fontSize="small" />,
  },
  {
    name: "Holiday",
    key: "total_assigned_holiday_count",
    type: "number",
    icon: <CelebrationRounded fontSize="small" />,
  },
  {
    name: "Week Off",
    key: "total_assigned_week_off_count",
    type: "number",
    icon: <WeekendRounded fontSize="small" />,
  },
  {
    name: "Late",
    key: "total_assigned_late_count",
    type: "number",
    icon: <WatchLaterRounded fontSize="small" />,
  },
  {
    name: "Working Hours",
    key: "total_working_hours",
    type: "time",
    icon: <AccessTimeRounded fontSize="small" />,
  },
  {
    name: "OT Hours",
    key: "total_ot_hours",
    type: "time",
    icon: <MoreTimeRounded fontSize="small" />,
  },
];

// The per-date expand table's identity columns — the day-level columns
// after them reuse workingHoursHeader (imported above), same set already
// used for this report's employee-wise expand panel.
const DETAILED_DATE_EXPAND_IDENTITY_HEADERS = [
  { name: "Employee Name", key: "user_name" },
  { name: "Employee Id", key: "employee_id" },
  { name: "Department", key: "department_name" },
];

const breakReportBreakHeaders = ["Break Start", "Break End", "Break-Time"];
const breakReportDateWiseDetailHeaders = [
  "Employee Name",
  "Employee Id",
  "Department Name",
  "Shift",
  "Check-In",
  "Check-Out",
  "Break",
];

// Identity columns line up 1:1 with breakReportFilterHeaders' keys (the ones
// a saved view can hide/reorder); the detail columns after them come from
// the per-user break drill-down and have no on-screen equivalent, so they're
// always included.
const BREAK_IDENTITY_EXPORT_COLUMNS = [
  { name: "Employee Name", key: "Employee Name", sourceKey: "employee_name" },
  { name: "Employee Id", key: "Employee Id", sourceKey: "employee_id" },
  {
    name: "Department Name",
    key: "Department Name",
    sourceKey: "department_name",
  },
  {
    name: "Total Break Hours",
    key: "Total Break Hours",
    sourceKey: "total_break_hours",
  },
];

// Identity columns line up 1:1 with PunchDetails.js's adjustmentReportHeader
// keys (the ones a saved view can hide/reorder); the detail columns after
// them come from the per-user punch drill-down and have no on-screen
// equivalent, so they're always included.
const ADJUSTMENT_IDENTITY_EXPORT_COLUMNS = [
  { name: "Employee Name", key: "Employee Name", sourceKey: "username" },
  { name: "Employee Id", key: "Employee Id", sourceKey: "employee_id" },
  {
    name: "Department Name",
    key: "Department Name",
    sourceKey: "department_name",
  },
  { name: "Punch Count", key: "Punch Count", sourceKey: "punch_in_count" },
];

const adjustmentReportDetailDownloadHeaders = [
  { name: "Date", key: "Date" },
  { name: "Shift", key: "Shift" },
  { name: "Original Time", key: "Original Time" },
  { name: "Adjusted Time", key: "Adjusted Time" },
  { name: "Modified By", key: "Modified By" },
  { name: "Modified At", key: "Modified At" },
];

const adjustmentIdentityValueGetters = {
  username: (employee) => employee?.username || employee?.employee_name || "-",
  employee_id: (employee) => employee?.employee_id || "-",
  department_name: (employee) => getCellValue(employee?.department_name, "-"),
  punch_in_count: (employee) => getCellValue(employee?.punch_in_count, 0),
};

const getCellValue = (value, fallback = "N/A") =>
  value || value === 0 ? value : fallback;

const getBreakStart = (breakRecord) =>
  getCellValue(
    breakRecord?.break_out ||
      breakRecord?.break_start ||
      breakRecord?.break_start_time,
  );

const getBreakEnd = (breakRecord) =>
  getCellValue(
    breakRecord?.break_in ||
      breakRecord?.break_end ||
      breakRecord?.break_end_time,
  );

const getBreakDuration = (breakRecord) =>
  getCellValue(
    breakRecord?.individual_break_time ||
      breakRecord?.break_duration ||
      breakRecord?.duration,
    "00:00",
  );

export const WorkingHours = ({
  setShowReport,
  reportName,
  colorCode,
  embeddedReportTypes = null,
  selectedReportType = null,
  onReportTypeChange = null,
  setReportHeaderActions = null,
}) => {
  const dispatch = useDispatch();
  const {
    token,
    domain,
    globalSearchValue,
    userDetails: currentUserDetails,
    userScreenAccess,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const {
    downloadExcel,
    downloadPdf,
    downloadCsv,
    downloadGroupedExcel,
    downloadGroupedPdf,
    downloadGroupedCsv,
  } = useExcelDownload();
  const [departmentIdValue, setDepartmentIdValue] = useState(defaultAllSelection);
  const { departmentId, departmentName } = useSelector(
    (state) => state.userReducer,
  );
  const normalizedReportName = normalizeReportType(reportName);
  const initialDateRange = getDefaultDateRange(normalizedReportName);
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState(
    initialDateRange.displayDateRange,
  );
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const [departmentData, setDepartmentData] = useState([]);
  const [removeExpand, setRemoveExpand] = useState(false);
  const [punchExports, setPunchExports] = useState(false);
  const [exportOTime, exportOverTimeReport] = useState(false);
  const [startDate, setStartDate] = useState(initialDateRange.startApiDate);
  const [endDate, setEndDate] = useState(initialDateRange.endApiDate);
  const [shiftCatagoryName, setShiftCatagoryName] = useState("");
  const [personExpandableId, setPersonExpandableId] = useState(0);
  const [breakExpandableDate, setBreakExpandableDate] = useState(0);
  const [breakExpandMode, setBreakExpandMode] = useState("collapse-all");
  const [breakExpandedEmployeeIds, setBreakExpandedEmployeeIds] = useState([]);
  const [breakExpandedSummaryKeys, setBreakExpandedSummaryKeys] = useState([]);
  const [breakExpandedDateKeys, setBreakExpandedDateKeys] = useState([]);
  const [breakDetailsByUserId, setBreakDetailsByUserId] = useState({});
  const [breakDateDetailsByDateKey, setBreakDateDetailsByDateKey] = useState(
    {},
  );
  // A single expanded date can hold thousands of employee rows even though
  // the outer date-wise table is paginated at 25 — this pages that inner
  // drill-down table independently so it never renders more than
  // BREAK_DATE_DETAIL_PAGE_SIZE rows into the DOM at once.
  const [breakDateDetailPageByKey, setBreakDateDetailPageByKey] = useState({});
  // Per-date search term for that same inner employee list — lets a user
  // jump straight to a name/id instead of paging through a busy date 25 at
  // a time.
  const [breakDateDetailSearchByKey, setBreakDateDetailSearchByKey] = useState(
    {},
  );
  const [breakDateLoadingKeys, setBreakDateLoadingKeys] = useState([]);
  const [breakLoadingUserIds, setBreakLoadingUserIds] = useState([]);
  const [detailedExpandMode, setDetailedExpandMode] = useState("collapse-all");
  const [detailedExpandedEmployeeIds, setDetailedExpandedEmployeeIds] =
    useState([]);
  const [detailedDetailsByUserId, setDetailedDetailsByUserId] = useState({});
  const [detailedLoadingUserIds, setDetailedLoadingUserIds] = useState([]);
  const detailedDetailsRef = useRef({});
  const detailedRequestsRef = useRef(new Set());
  const [detailedErrorUserIds, setDetailedErrorUserIds] = useState([]);
  useEffect(() => {
    detailedDetailsRef.current = detailedDetailsByUserId;
  }, [detailedDetailsByUserId]);
  // Detailed Working Hours' Date Wise view: one summary row per date from
  // users_working_hours_detailed_summary (department-wide aggregates, no
  // per-employee payload), each expandable into a per-date, server-paginated
  // employee list fetched from multiple_individual_working_hours with
  // start_date=end_date=that one date — deliberately NOT loaded in bulk up
  // front, since that endpoint's own pagination exists specifically to avoid
  // shipping every employee's full day-by-day array in one response.
  const [detailedDateSummaryRows, setDetailedDateSummaryRows] = useState([]);
  const [detailedDateSummaryLoading, setDetailedDateSummaryLoading] =
    useState(false);
  const [expandedDetailedDates, setExpandedDetailedDates] = useState([]);
  const [detailedDateExpandDataByDate, setDetailedDateExpandDataByDate] =
    useState({});
  const [detailedDateExpandMode, setDetailedDateExpandMode] =
    useState("collapse-all");
  // Client-side filter of the currently loaded page's employees for a given
  // expanded date — matches Break/Punch/Time Card's per-row search box.
  const [detailedDateExpandSearchByKey, setDetailedDateExpandSearchByKey] =
    useState({});
  const [reportedType, setReportedType] = useState(normalizedReportName);
  const [workingHoursDetails, setWorkingHoursDetails] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [page, setLeavePage] = useState(0);
  const [rowsPerPage, setLeaveRowsPerPage] = useState(25);
  const [punchDetailsData, setPunchDetailsData] = useState([]);
  const [masterPunchDetails, setmasterPunchDetails] = useState([]);
  // null until PunchDetails reports in — treated as "show every identity
  // column" so the very first export (before that effect has fired) still
  // behaves like it did before saved views existed. Shared by both Punch
  // Entry and Adjustment Entry modes — they use the same identity key space
  // (username/employee_id/department_name/punch_in_count) and only one mode
  // is ever mounted at a time.
  const [punchVisibleHeaderKeys, setPunchVisibleHeaderKeys] = useState(null);
  const [, setUserLoader] = useState(false);
  const [shiftDetails, setShiftDetails] = useState({});
  const [openSidePeek, setOpenSidePeek] = useState(false);
  const [editState, setEditState] = useState(false);
  const [fromDate, setFromDate] = useState(initialDateRange.startApiDate);
  const [toDate, setToDate] = useState(initialDateRange.endApiDate);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [fetchSummarizedReport, setFetchSummarizedReport] = useState(true);
  const [fetchPunchDetails, setFetchPunchDetails] = useState(true);
  const [fetchTimeCardReport, setFetchTimeCardReport] = useState(true);
  const [workingHourData, setWorkingHourData] = useState([]);
  // users_working_hours_user_details sends grand totals (present/absent/
  // holiday/week-off/OT-hours across every employee in the date range)
  // alongside its per-employee `results` array — captured here so
  // timeCardSummary can use the backend's own numbers for "All" instead of
  // re-deriving them client-side.
  const [timeCardApiTotals, setTimeCardApiTotals] = useState(null);
  const [breakViewMode, setBreakViewMode] = useState(
    BREAK_VIEW_MODE_OPTIONS[0],
  );
  const isBreakDateWiseView = breakViewMode?.id === "date-wise";
  const [punchViewMode, setPunchViewMode] = useState(
    PUNCH_VIEW_MODE_OPTIONS[0],
  );
  const [timeCardViewMode, setTimeCardViewMode] = useState(
    TIME_CARD_VIEW_MODE_OPTIONS[0],
  );
  const [detailedViewMode, setDetailedViewMode] = useState(
    DETAILED_VIEW_MODE_OPTIONS[0],
  );
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isPreparingDetailedData, setIsPreparingDetailedData] = useState(false);
  const [membersData, setMembersData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState({
    id: defaultAllSelection,
    label: "All",
    user_id: defaultAllSelection,
    user_name: "All",
    employee_id: "",
  });
  const skipInitialCompanyReloadRef = useRef(true);
  const lastEmployeePrefillDepartmentRef = useRef(null);
  const breakDateDetailsRequestRef = useRef({});
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const resetBreakReportExpansion = useCallback(() => {
    setBreakExpandMode("collapse-all");
    setBreakExpandedEmployeeIds([]);
    setBreakExpandedSummaryKeys([]);
    setBreakExpandedDateKeys([]);
    setBreakDetailsByUserId({});
    setBreakDateDetailsByDateKey({});
    setBreakDateDetailPageByKey({});
    setBreakDateDetailSearchByKey({});
    setBreakDateLoadingKeys([]);
    setBreakLoadingUserIds([]);
  }, []);
  const resetDetailedReportExpansion = useCallback(() => {
    detailedDetailsRef.current = {};
    detailedRequestsRef.current = new Set();
    setDetailedErrorUserIds([]);
    setDetailedExpandMode("collapse-all");
    setDetailedExpandedEmployeeIds([]);
    setDetailedDetailsByUserId({});
    setDetailedLoadingUserIds([]);
  }, []);
  const { result: colorCodeData } = useFetch(
    `${domain}attendance/status-colors/`,
  );

  const effectiveColorCode =
    Array.isArray(colorCode) && colorCode.length > 0
      ? colorCode
      : colorCodeData;
  const shouldIncludeCurrentDate =
    isPunchBasedReport(reportedType) || reportedType === RAW_PUNCH_ENTRY_TYPE;
  const reportTypeOptions = React.useMemo(() => {
    if (embeddedReportTypes?.length && onReportTypeChange) {
      return embeddedReportTypes;
    }

    if (
      isPunchBasedReport(normalizedReportName) ||
      normalizedReportName === RAW_PUNCH_ENTRY_TYPE
    ) {
      return reports.filter(
        (report) =>
          report.label === PUNCH_DETAILS_TYPE ||
          report.label === ADJUSTMENT_REPORT_TYPE ||
          report.label === RAW_PUNCH_ENTRY_TYPE,
      );
    }

    return reports.filter(
      (report) =>
        report.label !== PUNCH_DETAILS_TYPE &&
        report.label !== ADJUSTMENT_REPORT_TYPE &&
        report.label !== RAW_PUNCH_ENTRY_TYPE,
    );
  }, [embeddedReportTypes, normalizedReportName, onReportTypeChange]);
  const supportsSavedViews =
    reportedType === DETAILED_WORKING_HOURS_TYPE ||
    reportedType === SUMMARIZED_WORKING_HOURS_TYPE;
  const employeeOptions = React.useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        user_id: defaultAllSelection,
        user_name: "All",
        employee_id: "",
      },
      ...(membersData
        ?.filter((obj) => obj?.user_name)
        ?.map((obj) => ({
          ...obj,
          id: obj?.user_id,
          label: obj?.user_name,
          user_name: obj?.user_name,
        })) ?? []),
    ],
    [membersData],
  );

  const statusLabelMap = React.useMemo(() => {
    if (!Array.isArray(effectiveColorCode)) return {};
    return effectiveColorCode.reduce((acc, item) => {
      const labelValue = item.label ?? item.label_name ?? item.name ?? "";
      if (item.status) acc[item.status] = labelValue;
      if (item.legend) acc[item.legend] = labelValue;
      if (item.code) acc[item.code] = labelValue;
      if (item.type) acc[item.type] = labelValue;
      return acc;
    }, {});
  }, [effectiveColorCode]);

  const getStatusTooltip = (status) => {
    if (!status) return "";
    const label = statusLabelMap[status];
    return label ? `${status} — ${label}` : status;
  };

  useEffect(() => {
    setReportedType(normalizedReportName);
    setPersonExpandableId(0);
    setRemoveExpand(true);
    resetBreakReportExpansion();
    resetDetailedReportExpansion();

    if (isPunchBasedReport(normalizedReportName)) {
      setFetchPunchDetails(true);
    } else if (normalizedReportName === TIME_CARD_REPORT_TYPE) {
      setFetchSummarizedReport(true);
    } else if (
      normalizedReportName !== WORKING_HOURS_REPORT_TYPE &&
      normalizedReportName !== RAW_PUNCH_ENTRY_TYPE
    ) {
      setFetchSummarizedReport(true);
    }
  }, [
    normalizedReportName,
    resetBreakReportExpansion,
    resetDetailedReportExpansion,
  ]);

  const timeCardSummary = React.useMemo(() => {
    const selectedEmployeeId = `${selectedEmployee?.user_id || ""}`;
    const isAllSelected =
      !selectedEmployeeId || selectedEmployeeId === `${defaultAllSelection}`;
    const relevantRows = (
      Array.isArray(workingHoursDetails) ? workingHoursDetails : []
    ).filter((item) => {
      if (reportedType !== TIME_CARD_REPORT_TYPE) {
        return false;
      }

      return (
        isAllSelected ||
        `${item?.user_id || item?.id || ""}` === selectedEmployeeId
      );
    });

    const counts = relevantRows.reduce(
      (acc, item) => {
        const dayRecords = getEmployeeDayRecords(item);
        const dayTally = dayRecords.reduce(
          (tally, day) => {
            const status = classifyDayStatus(day);
            if (status) tally[status] += 1;
            return tally;
          },
          { present: 0, absent: 0, holiday: 0, weeklyOff: 0 },
        );

        acc.present += resolveDayCount(item?.present_count, dayTally.present);
        acc.holiday += resolveDayCount(item?.holiday_count, dayTally.holiday);
        acc.absent += resolveDayCount(item?.absent_count, dayTally.absent);
        acc.weeklyOff += resolveDayCount(
          item?.week_off_count,
          dayTally.weeklyOff,
        );
        acc.otHours.push(
          item?.total_ot_hours ||
            sumHhMmValues(dayRecords.map((day) => day?.over_time)),
        );
        return acc;
      },
      { present: 0, holiday: 0, absent: 0, weeklyOff: 0, otHours: [] },
    );

    // users_working_hours_user_details also sends grand totals for the
    // whole date range (total_assigned_present_count, total_ot_hours, etc)
    // alongside its per-employee rows. Those are the backend's own numbers
    // rather than a client reconstruction, so prefer them whenever every
    // employee is in view; a single selected employee still needs the
    // per-row tally above since the API totals cover everyone.
    const useApiTotals = isAllSelected && timeCardApiTotals;

    return {
      "Total Present": `${
        useApiTotals
          ? resolveDayCount(timeCardApiTotals.present, counts.present)
          : counts.present
      }`,
      "Total Holiday": `${
        useApiTotals
          ? resolveDayCount(timeCardApiTotals.holiday, counts.holiday)
          : counts.holiday
      }`,
      "Total Absent": `${
        useApiTotals
          ? resolveDayCount(timeCardApiTotals.absent, counts.absent)
          : counts.absent
      }`,
      "Total Weekly Off": `${
        useApiTotals
          ? resolveDayCount(timeCardApiTotals.weeklyOff, counts.weeklyOff)
          : counts.weeklyOff
      }`,
      "Total Overtime":
        useApiTotals && timeCardApiTotals.otHours
          ? timeCardApiTotals.otHours
          : sumHhMmValues(counts.otHours),
    };
  }, [
    reportedType,
    selectedEmployee?.user_id,
    timeCardApiTotals,
    workingHoursDetails,
  ]);

  // Same StatCard row Summary/Detailed already show above their table
  // (workingHoursSummaryCards below) — Time Card had the underlying counts
  // computed via timeCardSummary all along (it's exported into the report's
  // download metadata) but never actually rendered them on screen.
  const timeCardSummaryCards = useMemo(
    () => [
      {
        key: "present",
        title: "Total Present",
        value: timeCardSummary["Total Present"],
        icon: EventAvailableRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "absent",
        title: "Total Absent",
        value: timeCardSummary["Total Absent"],
        icon: EventBusyRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
      {
        key: "holiday",
        title: "Total Holiday",
        value: timeCardSummary["Total Holiday"],
        icon: CelebrationRounded,
        accent: "#B45309",
        tint: "#FFFBEB",
      },
      {
        key: "weeklyOff",
        title: "Total Weekly Off",
        value: timeCardSummary["Total Weekly Off"],
        icon: WeekendRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "overtime",
        title: "Total Overtime",
        value: timeCardSummary["Total Overtime"],
        icon: MoreTimeRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
    ],
    [timeCardSummary],
  );

  // Break Report never had a StatCard row (it's a separate render path from
  // Summary/Detailed/Time Card's shared one), so it's built self-contained
  // here from the same workingHourData the table itself renders.
  const breakSummaryCards = useMemo(
    () => [
      {
        key: isBreakDateWiseView ? "dates" : "employees",
        title: isBreakDateWiseView ? "Dates" : "Employees",
        value: workingHourData?.length || 0,
        icon: isBreakDateWiseView ? ScheduleRounded : Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "totalBreakHours",
        title: "Total Break Hours",
        value: sumHhMmValues(
          (workingHourData || []).map((row) => row?.total_break_hours),
        ),
        icon: FreeBreakfastRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
    ],
    [isBreakDateWiseView, workingHourData],
  );

  useEffect(() => {
    if (reportedType !== TIME_CARD_REPORT_TYPE) {
      return;
    }

    if (!employeeOptions.length) {
      lastEmployeePrefillDepartmentRef.current = departmentId;
      setSelectedEmployee({
        id: defaultAllSelection,
        label: "All",
        user_id: defaultAllSelection,
        user_name: "All",
        employee_id: "",
      });
      return;
    }

    const existingEmployee = employeeOptions.find(
      (employee) => `${employee.user_id}` === `${selectedEmployee?.user_id}`,
    );

    if (
      lastEmployeePrefillDepartmentRef.current !== departmentId ||
      !existingEmployee
    ) {
      lastEmployeePrefillDepartmentRef.current = departmentId;
      setSelectedEmployee({
        id: defaultAllSelection,
        label: "All",
        user_id: defaultAllSelection,
        user_name: "All",
        employee_id: "",
      });
      setLeavePage(0);
      setFetchTimeCardReport(true);
    }
  }, [departmentId, employeeOptions, reportedType, selectedEmployee?.user_id]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!topbarCompanyId) return;

      try {
        const response = await fetch(
          `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${departmentId || defaultAllSelection}&status=Active`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();

        if (response.ok) {
          setMembersData(Array.isArray(res) ? res : []);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        setMembersData([]);
        showToast(
          "error",
          "Couldn't load the employee list. Please try again.",
        );
      }
    };

    if (reportedType === TIME_CARD_REPORT_TYPE) {
      fetchEmployees();
    }
  }, [departmentId, domain, reportedType, token, topbarCompanyId]);

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );
  const moduleName =
    reportedType === RAW_PUNCH_ENTRY_TYPE
      ? RAW_PUNCH_ENTRY_TYPE
      : reportedType === WORKING_HOURS_REPORT_TYPE
        ? "Working Hours Report"
        : reportedType === DETAILED_WORKING_HOURS_TYPE ||
            reportedType === BREAK_REPORT_TYPE
          ? "Detailed Working Hours"
          : reportedType === TIME_CARD_REPORT_TYPE
            ? "Time Card Report"
            : isPunchBasedReport(reportedType)
              ? reportedType
              : "Summarized Working Hours";

  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, moduleName),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    moduleName,
  );

  const handleSavedViewChange = useCallback(() => {
    setLeavePage(0);
  }, []);

  const updatePunchViewMode = useCallback((event, option) => {
    if (!option?.id) return;
    setPunchViewMode(option);
    setLeavePage(0);
    // Date-wise rows are naturally bounded (a month's worth of days), so a
    // smaller page size reads better than the 25 used for potentially
    // thousands of employees in employee-wise view.
    setLeaveRowsPerPage(option.id === "date-wise" ? 10 : 25);
    setRemoveExpand(true);
  }, []);

  const updateTimeCardViewMode = useCallback((event, option) => {
    if (!option?.id) return;
    setTimeCardViewMode(option);
    setLeavePage(0);
    // Date-wise rows are naturally bounded (a month's worth of days), so a
    // smaller page size reads better than the 25 used for potentially
    // thousands of employees in employee-wise view.
    setLeaveRowsPerPage(option.id === "date-wise" ? 10 : 25);
  }, []);

  const updateDetailedViewMode = useCallback((event, option) => {
    if (!option?.id) return;
    setDetailedViewMode(option);
    setLeavePage(0);
    setLeaveRowsPerPage(option.id === "date-wise" ? 10 : 25);
  }, []);

  useEffect(() => {
    if (reportedType !== BREAK_REPORT_TYPE) {
      return;
    }

    setBreakViewMode(BREAK_VIEW_MODE_OPTIONS[0]);
  }, [reportedType]);

  useEffect(() => {
    if (!isPunchBasedReport(reportedType)) {
      return;
    }

    setPunchViewMode(PUNCH_VIEW_MODE_OPTIONS[0]);
  }, [reportedType]);

  useEffect(() => {
    if (reportedType !== DETAILED_WORKING_HOURS_TYPE) {
      return;
    }

    setDetailedViewMode(DETAILED_VIEW_MODE_OPTIONS[0]);
  }, [reportedType]);

  const mainHeaderKeys = useMemo(
    () => new Set(workingHoursHeaders.map((header) => header.key)),
    [],
  );

  const combinedFullHeaders = useMemo(() => {
    const mainHeaders = workingHoursHeaders.map((header) => ({
      ...header,
      name: formatWorkingHoursHeaderName(header.key, header.name),
      groupName:
        reportedType === DETAILED_WORKING_HOURS_TYPE ? "Main Table" : null,
    }));

    if (reportedType !== DETAILED_WORKING_HOURS_TYPE) return mainHeaders;

    const expandHeaders = workingHoursHeader.map((header) => ({
      ...header,
      name: formatWorkingHoursHeaderName(header.key, header.name),
      groupName: "Expand Panel",
    }));

    return [...mainHeaders, ...expandHeaders];
  }, [reportedType]);

  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: currentUserDetails?.id,
    subModuleId,
    storageKey: DETAILED_WORKING_HOURS_SAVED_VIEW_KEY,
    scope: reportedType,
    onViewChange: handleSavedViewChange,
    defaultViewId: WORKING_HOURS_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || WORKING_HOURS_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== WORKING_HOURS_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => combinedFullHeaders,
    [combinedFullHeaders],
  );

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === WORKING_HOURS_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== WORKING_HOURS_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(
    () =>
      (storedDefaultView?.isCustomDefault ? storedDefaultView : null) || {
        id: WORKING_HOURS_DEFAULT_VIEW_ID,
        name: "Default View",
        headerConfig: baseDefaultHeaders.map((header) => ({
          key: header.key,
          name: header.name,
        })),
        headerKeys: baseDefaultHeaders.map((header) => header.key),
        isDefaultView: true,
      },
    [baseDefaultHeaders, storedDefaultView],
  );

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === WORKING_HOURS_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const combinedVisibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      combinedFullHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: formatWorkingHoursHeaderName(
              entry.key,
              entry.name?.trim() || baseHeader.name,
            ),
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = combinedFullHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, combinedFullHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(WORKING_HOURS_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === WORKING_HOURS_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(WORKING_HOURS_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(WORKING_HOURS_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || WORKING_HOURS_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(WORKING_HOURS_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () =>
      supportsSavedViews ? (
        <SavedViewSelector
          headers={combinedFullHeaders}
          defaultHeaders={baseDefaultHeaders}
          defaultView={defaultView}
          savedViews={customSavedViews}
          activeView={activeView}
          activeViewId={effectiveViewId}
          persistedDefaultViewId={persistedDefaultViewId}
          screenAccess={check_access}
          onSelectDefaultView={handleSelectDefaultView}
          onSelectView={handleSelectView}
          onCreateView={handleCreateView}
          onUpdateView={(viewId, viewName, headerConfig) => {
            if (viewId === WORKING_HOURS_DEFAULT_VIEW_ID) {
              handleEditDefaultView(viewId, viewName, headerConfig);
              return;
            }
            updateView(viewId, viewName, headerConfig);
          }}
          onDeleteView={handleDeleteView}
          onMakeDefaultView={handleMakeDefaultView}
        />
      ) : null,
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      combinedFullHeaders,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      persistedDefaultViewId,
      supportsSavedViews,
      updateView,
    ],
  );

  const newButtonComponent = null;

  const visibleHeaders = useMemo(
    () =>
      combinedVisibleHeaders.filter((header) => mainHeaderKeys.has(header.key)),
    [combinedVisibleHeaders, mainHeaderKeys],
  );

  // getWorkingHoursColumnWidth's numbers (220 for employee_name, 100 for a
  // plain hours count, ...) were being used as literal pixel widths on the
  // header cells — under table-layout:"fixed", a table's rendered width is
  // the GREATER of its own declared width and the sum of its column
  // widths, not the declared width with columns compressed to fit. With
  // 14-15 columns those pixel sums easily exceeded the container, and
  // since this table intentionally has no scroll container of its own
  // (single page-level scroll, matching Admin's Users screen — see the
  // TableContainer comment below), that overflow had nowhere to go but
  // .main-content itself, dragging the whole page (breadcrumb, stat
  // cards, toolbar included) into horizontal scroll together.
  // Converting each column's weight into a % of the total, rather than a
  // literal px count, makes the columns always sum to exactly 100% —
  // mathematically incapable of overflowing — while keeping the same
  // relative proportions (name gets more room than an hours count)
  // regardless of how many columns happen to be visible.
  const visibleHeaderColumnWidthTotal = useMemo(() => {
    const toggleWeight = reportedType === DETAILED_WORKING_HOURS_TYPE ? 48 : 0;
    return (
      toggleWeight +
      visibleHeaders.reduce(
        (total, header) => total + getWorkingHoursColumnWidth(header),
        0,
      )
    );
  }, [visibleHeaders, reportedType]);

  const getWorkingHoursColumnWidthPercent = (weight) =>
    `${((weight / visibleHeaderColumnWidthTotal) * 100).toFixed(3)}%`;

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [
    customFilterNode,
    reportedType,
    workingHourData.length,
    visibleHeaders.length,
  ]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const expandVisibleHeaders = useMemo(
    () =>
      reportedType === DETAILED_WORKING_HOURS_TYPE
        ? combinedVisibleHeaders.filter(
            (header) => !mainHeaderKeys.has(header.key),
          )
        : [],
    [combinedVisibleHeaders, mainHeaderKeys, reportedType],
  );

  const {
    visibleHeaders: breakVisibleHeaders,
    titleOverride: breakTitleOverride,
  } = useSavedViewSelector({
    domain,
    userId: currentUserDetails?.id,
    subModuleId,
    storageKey: BREAK_REPORT_SAVED_VIEW_KEY,
    fullHeaders: breakReportFilterHeaders,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });
  const isDetailedDateWiseView =
    reportedType === DETAILED_WORKING_HOURS_TYPE &&
    detailedViewMode?.id === "date-wise";
  // Groups every employee's daily break detail by normalized date, once, in
  // a single O(employees x details) pass. Both breakDateRows below and
  // ensureBreakReportDateDetails used to redo this same flatMap+moment-parse
  // scan of the *entire* employee list independently — breakDateRows once
  // per date in the range, and ensureBreakReportDateDetails once per date
  // every time a row was expanded. With thousands of employees loaded,
  // Expand All (25 dates per page) meant 25 repeats of that full scan on
  // every click, which is what hung the tab.
  const workingHoursDetailsByDate = useMemo(() => {
    const map = new Map();
    (Array.isArray(workingHoursDetails) ? workingHoursDetails : []).forEach(
      (employee) => {
        (Array.isArray(employee?.user_details)
          ? employee.user_details
          : []
        ).forEach((detail) => {
          const normalizedDate = moment(
            detail?.date,
            ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
            true,
          ).format("YYYY-MM-DD");
          const mergedDetail = { ...employee, ...detail };
          const existing = map.get(normalizedDate);
          if (existing) {
            existing.push(mergedDetail);
          } else {
            map.set(normalizedDate, [mergedDetail]);
          }
        });
      },
    );
    return map;
  }, [workingHoursDetails]);
  const breakDateRows = useMemo(() => {
    const parsedStartDate = moment(startDate, "YYYY-MM-DD", true);
    const parsedEndDate = moment(endDate, "YYYY-MM-DD", true);

    if (
      !parsedStartDate.isValid() ||
      !parsedEndDate.isValid() ||
      parsedStartDate.isAfter(parsedEndDate, "day")
    ) {
      return [];
    }

    const rows = [];
    const cursor = parsedStartDate.clone();
    while (!cursor.isAfter(parsedEndDate, "day")) {
      const dateKey = cursor.format("YYYY-MM-DD");
      const matchingDetails = workingHoursDetailsByDate.get(dateKey) || [];
      rows.push({
        row_id: `break-date:${dateKey}`,
        date_key: dateKey,
        date: cursor.format("DD/MM/YYYY"),
        employee_count: matchingDetails.length,
        total_break_hours: sumHhMmValues(
          matchingDetails.map((detail) => detail?.break_hours),
        ),
      });
      cursor.add(1, "day");
    }

    return rows;
  }, [endDate, startDate, workingHoursDetailsByDate]);
  const breakActiveHeaders = isBreakDateWiseView
    ? breakReportDateWiseHeaders
    : breakVisibleHeaders;
  const breakTitleControl = isBreakDateWiseView ? null : breakTitleOverride;
  const breakSourceRows = isBreakDateWiseView
    ? breakDateRows
    : workingHoursDetails;
  const visibleBreakRows = useMemo(
    () =>
      (workingHourData || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [page, rowsPerPage, workingHourData],
  );

  // O(1) lookup instead of `.find()`ing the full (potentially thousands-long)
  // employee list on every expand — see the Break/Punch/Adjustment browser
  // hang investigation: expanding N rows used to mean N linear scans plus N
  // separate state updates over the whole dataset.
  const workingHoursDetailsByUserId = useMemo(() => {
    const map = new Map();
    (Array.isArray(workingHoursDetails) ? workingHoursDetails : []).forEach(
      (row) => {
        if (row?.user_id !== undefined && row?.user_id !== null) {
          map.set(`${row.user_id}`, row);
        }
      },
    );
    return map;
  }, [workingHoursDetails]);

  const ensureBreakReportUserDetails = useCallback(
    async (userId, { silent = false } = {}) => {
      if (!userId) return null;
      if (breakDetailsByUserId[userId]) {
        return breakDetailsByUserId[userId];
      }
      if (breakLoadingUserIds.includes(userId)) {
        return null;
      }

      const embeddedEmployee = workingHoursDetailsByUserId.get(`${userId}`);
      const embeddedDetails = Array.isArray(embeddedEmployee?.user_details)
        ? embeddedEmployee.user_details
        : [];
      const embeddedResponse = {
        ...embeddedEmployee,
        checkin_details: embeddedDetails,
      };

      if (embeddedEmployee) {
        setBreakDetailsByUserId((prev) => ({
          ...prev,
          [userId]: embeddedResponse,
        }));
        return embeddedResponse;
      }

      setBreakLoadingUserIds((prev) => [...prev, userId]);
      try {
        const response = await fetch(
          `${domain}users_working_hours_user_details/?user_id=${userId}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setBreakDetailsByUserId((prev) => ({
            ...prev,
            [userId]: res,
          }));
          return res;
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[BreakReport] operation failed:", error);
        if (!silent) {
          showToast(
            "error",
            "Couldn't load the break report details. Please try again.",
          );
        }
      } finally {
        setBreakLoadingUserIds((prev) => prev.filter((id) => id !== userId));
      }

      return null;
    },
    [
      breakDetailsByUserId,
      breakLoadingUserIds,
      departmentId,
      domain,
      endDate,
      startDate,
      token,
      workingHoursDetailsByUserId, topbarCompanyId
    ],
  );

  const extractBreakDateDetailRows = useCallback((responsePayload) => {
    const normalizeBreakDateDetailRow = (row) => {
      if (!row || typeof row !== "object") {
        return row;
      }

      const dayDetails =
        row?.days && typeof row.days === "object" && !Array.isArray(row.days)
          ? row.days
          : null;

      if (!dayDetails) {
        return row;
      }

      return {
        ...row,
        ...dayDetails,
        employee_name: row?.employee_name || row?.user_name || row?.username,
        break_details: Array.isArray(dayDetails?.break_details)
          ? dayDetails.break_details
          : Array.isArray(row?.break_details)
            ? row.break_details
            : [],
        permission_details: Array.isArray(dayDetails?.permission_details)
          ? dayDetails.permission_details
          : Array.isArray(row?.permission_details)
            ? row.permission_details
            : [],
      };
    };

    const rows = Array.isArray(responsePayload)
      ? responsePayload
      : Array.isArray(responsePayload?.results)
        ? responsePayload.results
        : Array.isArray(responsePayload?.data)
          ? responsePayload.data
          : Array.isArray(responsePayload?.responseDetails)
            ? responsePayload.responseDetails
            : Array.isArray(responsePayload?.checkin_details)
              ? responsePayload.checkin_details
              : [];

    return rows.map(normalizeBreakDateDetailRow);
  }, []);

  const ensureBreakReportDateDetails = useCallback(
    async (dateKey, { silent = false } = {}) => {
      if (!dateKey) return [];
      if (breakDateDetailsByDateKey[dateKey]) {
        return breakDateDetailsByDateKey[dateKey];
      }

      const embeddedDateRows = workingHoursDetailsByDate.get(dateKey) || [];

      if (Array.isArray(workingHoursDetails)) {
        const normalizedEmbeddedRows =
          extractBreakDateDetailRows(embeddedDateRows);
        setBreakDateDetailsByDateKey((prev) => ({
          ...prev,
          [dateKey]: normalizedEmbeddedRows,
        }));
        return normalizedEmbeddedRows;
      }

      if (breakDateDetailsRequestRef.current[dateKey]) {
        return breakDateDetailsRequestRef.current[dateKey];
      }
      if (breakDateLoadingKeys.includes(dateKey)) {
        return [];
      }

      const requestPromise = (async () => {
        setBreakDateLoadingKeys((prev) =>
          prev.includes(dateKey) ? prev : [...prev, dateKey],
        );
        try {
          let currentPage = 1;
          let totalPages = 1;
          const collectedRows = [];

          while (currentPage <= totalPages) {
            const response = await fetch(
              `${domain}individual_details_working_hours_by_date/?user_id=${currentUserDetails.id}&date=${dateKey}&page=${currentPage}&company_id=${topbarCompanyId}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `token ${token}`,
                },
              },
            );
            const res = await response.json();

            if (!response.ok) {
              if (response.status === 409) {
                sessionHandling();
                return [];
              }

              throw new Error("REQUEST_FAILED");
            }

            collectedRows.push(...extractBreakDateDetailRows(res));

            const resolvedTotalPages = Number(
              res?.pagination?.total_pages ?? res?.total_pages ?? totalPages,
            );
            totalPages =
              Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0
                ? resolvedTotalPages
                : totalPages;

            const nextPage = Number(
              res?.pagination?.next_page ?? res?.next_page ?? null,
            );
            if (
              !Number.isFinite(nextPage) ||
              nextPage <= currentPage ||
              currentPage >= totalPages
            ) {
              break;
            }

            currentPage = nextPage;
          }

          setBreakDateDetailsByDateKey((prev) => ({
            ...prev,
            [dateKey]: collectedRows,
          }));
          return collectedRows;
        } catch (error) {
          console.error("[BreakReportDateWise] operation failed:", error);
          if (!silent) {
            showToast(
              "error",
              "Couldn't load the break report date-wise details. Please try again.",
            );
          }
          return [];
        } finally {
          delete breakDateDetailsRequestRef.current[dateKey];
          setBreakDateLoadingKeys((prev) =>
            prev.filter((key) => key !== dateKey),
          );
        }
      })();

      breakDateDetailsRequestRef.current[dateKey] = requestPromise;
      return requestPromise;
    },
    [
      breakDateDetailsByDateKey,
      breakDateLoadingKeys,
      currentUserDetails.id,
      domain,
      extractBreakDateDetailRows,
      token,
      workingHoursDetails,
      workingHoursDetailsByDate, topbarCompanyId
    ],
  );

  useEffect(() => {
    if (
      reportedType !== BREAK_REPORT_TYPE ||
      breakExpandMode !== "expand-all"
    ) {
      return;
    }

    const nextExpandedIds = visibleBreakRows
      .map((row) =>
        isBreakDateWiseView ? row?.date_key || row?.row_id : row?.user_id,
      )
      .filter(Boolean);
    if (isBreakDateWiseView) {
      setBreakExpandedSummaryKeys(nextExpandedIds);

      if (Array.isArray(workingHoursDetails)) {
        // Same batching as the employee-wise branch below — one state
        // update for every already-embedded date instead of one per date.
        const embeddedDatePatch = {};
        nextExpandedIds.forEach((dateKey) => {
          if (breakDateDetailsByDateKey[dateKey]) return;
          embeddedDatePatch[dateKey] = extractBreakDateDetailRows(
            workingHoursDetailsByDate.get(dateKey) || [],
          );
        });
        if (Object.keys(embeddedDatePatch).length) {
          setBreakDateDetailsByDateKey((prev) => ({
            ...prev,
            ...embeddedDatePatch,
          }));
        }
      } else {
        nextExpandedIds.forEach((dateKey) => {
          ensureBreakReportDateDetails(dateKey, { silent: true });
        });
      }
      return;
    }

    setBreakExpandedEmployeeIds(nextExpandedIds);

    // Batch every already-embedded employee into one state update instead of
    // one setBreakDetailsByUserId call per row — with thousands of employees
    // loaded, N separate updates each re-ran every full-dataset memo N times.
    const embeddedPatch = {};
    const idsNeedingFetch = [];
    nextExpandedIds.forEach((userId) => {
      if (breakDetailsByUserId[userId]) return;
      const embeddedEmployee = workingHoursDetailsByUserId.get(`${userId}`);
      if (embeddedEmployee) {
        embeddedPatch[userId] = {
          ...embeddedEmployee,
          checkin_details: Array.isArray(embeddedEmployee?.user_details)
            ? embeddedEmployee.user_details
            : [],
        };
      } else {
        idsNeedingFetch.push(userId);
      }
    });

    if (Object.keys(embeddedPatch).length) {
      setBreakDetailsByUserId((prev) => ({ ...prev, ...embeddedPatch }));
    }
    idsNeedingFetch.forEach((userId) => {
      ensureBreakReportUserDetails(userId, { silent: true });
    });
  }, [
    breakDateDetailsByDateKey,
    breakDetailsByUserId,
    breakExpandMode,
    ensureBreakReportDateDetails,
    ensureBreakReportUserDetails,
    extractBreakDateDetailRows,
    isBreakDateWiseView,
    reportedType,
    visibleBreakRows,
    workingHoursDetails,
    workingHoursDetailsByDate,
    workingHoursDetailsByUserId,
  ]);

  const updateBreakExpandMode = useCallback((event, data) => {
    const nextMode = data?.id;
    if (!nextMode) return;

    setBreakExpandMode(nextMode);
    if (nextMode === "collapse-all") {
      setBreakExpandedEmployeeIds([]);
      setBreakExpandedSummaryKeys([]);
      setBreakExpandedDateKeys([]);
    }
  }, []);

  const updateBreakViewMode = useCallback(
    (event, option) => {
      if (!option?.id) return;
      setBreakViewMode(option);
      resetBreakReportExpansion();
      setLeavePage(0);
      // Date-wise rows are naturally bounded (a month's worth of days), so a
      // smaller page size reads better than the 25 used for potentially
      // thousands of employees in employee-wise view.
      setLeaveRowsPerPage(option.id === "date-wise" ? 10 : 25);
    },
    [resetBreakReportExpansion],
  );

  const breakExpandControl = useMemo(
    () =>
      reportedType === BREAK_REPORT_TYPE ? (
        <CompactHeaderSelectFilter
          dataList={BREAK_EXPAND_OPTIONS}
          selectedValue={
            BREAK_EXPAND_OPTIONS.find(
              (option) => option.id === breakExpandMode,
            ) || BREAK_EXPAND_OPTIONS[0]
          }
          updateDataFunction={updateBreakExpandMode}
          staticLabel=""
          Icons={MoreTimeRounded}
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 140 },
            maxWidth: { xs: "100%", sm: 140 },
          }}
          buttonSx={{
            minHeight: 40,
            height: 40,
            px: 1,
            borderRadius: 1.5,
            backgroundColor: designColors.white,
            border: `1px solid ${designColors.slate200}`,
            boxShadow: "none",
            color: designColors.slate700,
            "&:hover": {
              backgroundColor: designColors.slateBg,
              border: `1px solid ${designColors.slate300}`,
              boxShadow: "none",
            },
          }}
        />
      ) : null,
    [breakExpandMode, reportedType, updateBreakExpandMode],
  );

  const ensureDetailedUserDetails = useCallback(
    async (userId, { silent = false } = {}) => {
      if (!userId) return null;
      if (detailedDetailsRef.current[userId]) {
        return detailedDetailsRef.current[userId];
      }
      const requests = detailedRequestsRef.current;
      if (requests.has(userId)) {
        return null;
      }

      // Register synchronously so repeated expands cannot duplicate a request.
      requests.add(userId);
      setDetailedErrorUserIds((prev) => prev.filter((id) => id !== userId));
      setDetailedLoadingUserIds((prev) => [...prev, userId]);
      try {
        const response = await fetch(
          `${domain}individual_details_working_hours/?user_id=${userId}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (requests !== detailedRequestsRef.current) return null;
        if (response.ok) {
          detailedDetailsRef.current = {
            ...detailedDetailsRef.current,
            [userId]: res,
          };
          setDetailedDetailsByUserId((prev) => ({
            ...prev,
            [userId]: res,
          }));
          return res;
        } else if (response.status === 409) {
          setDetailedErrorUserIds((prev) => [...prev, userId]);
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        if (requests !== detailedRequestsRef.current) return null;
        setDetailedErrorUserIds((prev) => [...prev, userId]);
        console.error("[DetailedWorkingHours] operation failed:", error);
        if (!silent) {
          showToast(
            "error",
            "Couldn't load the attendance report. Please try again.",
          );
        }
      } finally {
        requests.delete(userId);
        if (requests === detailedRequestsRef.current) {
          setDetailedLoadingUserIds((prev) =>
            prev.filter((id) => id !== userId),
          );
        }
      }

      return null;
    },
    [domain, endDate, startDate, token, topbarCompanyId],
  );

  const visibleDetailedRows = useMemo(
    () =>
      (workingHourData || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [page, rowsPerPage, workingHourData],
  );

  useEffect(() => {
    if (
      reportedType !== DETAILED_WORKING_HOURS_TYPE ||
      detailedExpandMode !== "expand-all"
    ) {
      return;
    }

    const nextExpandedIds = visibleDetailedRows
      .map((row) => row?.user_id)
      .filter(Boolean);
    setDetailedExpandedEmployeeIds(nextExpandedIds);
    nextExpandedIds.forEach((userId) => {
      ensureDetailedUserDetails(userId, { silent: true });
    });
  }, [
    detailedExpandMode,
    ensureDetailedUserDetails,
    reportedType,
    visibleDetailedRows,
  ]);

  const updateDetailedExpandMode = useCallback((event, data) => {
    const nextMode = data?.id;
    if (!nextMode) return;

    setDetailedExpandMode(nextMode);
    setPersonExpandableId(0);
    if (nextMode === "collapse-all") {
      setDetailedExpandedEmployeeIds([]);
    }
  }, []);

  const detailedExpandControl = useMemo(
    () =>
      reportedType === DETAILED_WORKING_HOURS_TYPE ? (
        <CompactHeaderSelectFilter
          dataList={BREAK_EXPAND_OPTIONS}
          selectedValue={
            BREAK_EXPAND_OPTIONS.find(
              (option) => option.id === detailedExpandMode,
            ) || BREAK_EXPAND_OPTIONS[0]
          }
          updateDataFunction={updateDetailedExpandMode}
          staticLabel=""
          Icons={MoreTimeRounded}
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 140 },
            maxWidth: { xs: "100%", sm: 140 },
          }}
          buttonSx={{
            minHeight: 40,
            height: 40,
            px: 1,
            borderRadius: 1.5,
            backgroundColor: designColors.white,
            border: `1px solid ${designColors.slate200}`,
            boxShadow: "none",
            color: designColors.slate700,
            "&:hover": {
              backgroundColor: designColors.slateBg,
              border: `1px solid ${designColors.slate300}`,
              boxShadow: "none",
            },
          }}
        />
      ) : null,
    [detailedExpandMode, reportedType, updateDetailedExpandMode],
  );

  const getReportMetadata = useCallback(() => {
    const summaryRows = workingHourData || [];
    const summaryTotalHours = sumHhMmValues(
      summaryRows.map(
        (row) =>
          row?.total_working_hours ||
          row?.total_duty_hours ||
          row?.total_duty_ot_break_hours ||
          "00:00",
      ),
    );

    return {
      "Company Name": topbarCompanyName,
      Department: departmentName || "All",
      Employee:
        reportedType === TIME_CARD_REPORT_TYPE
          ? selectedEmployee?.user_name || "All"
          : undefined,
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      "Report Type": reportTypeMap[reportedType] || "Detailed Working Hours",
      ...(reportedType === TIME_CARD_REPORT_TYPE ? timeCardSummary : {}),
      // Detailed's date-wise rows are per-date aggregates
      // (total_assigned_present_count, ...), not the per-employee rows
      // (present_count, ...) this metadata block was built for — same field
      // mismatch as workingHoursSummaryCards above, and the same fix:
      // Present/Absent/Leave sum the same either way (total person-days for
      // the range), Employees takes the largest single date's headcount
      // instead of summing (would multiply-count people across days).
      ...(isDetailedDateWiseView
        ? {
            Employees: summaryRows.reduce(
              (max, row) =>
                Math.max(max, Number(row?.total_employee_count) || 0),
              0,
            ),
            "Present Days": summaryRows.reduce(
              (sum, row) =>
                sum + Number(row?.total_assigned_present_count || 0),
              0,
            ),
            "Absent Days": summaryRows.reduce(
              (sum, row) => sum + Number(row?.total_assigned_absent_count || 0),
              0,
            ),
            "Leave Days": summaryRows.reduce(
              (sum, row) => sum + Number(row?.total_assigned_leave_count || 0),
              0,
            ),
            "Total Hours": summaryTotalHours,
          }
        : reportedType === SUMMARIZED_WORKING_HOURS_TYPE
          ? {
              Employees: summaryRows.length,
              "Working Hrs": sumHhMmValues(
                summaryRows.map((row) => row?.total_working_hours),
              ),
              "Shift Hrs": sumHhMmValues(
                summaryRows.map((row) => row?.shift_working_hours),
              ),
              "OT Hrs": sumHhMmValues(summaryRows.map((row) => row?.ot_hours)),
              "Break Hrs": sumHhMmValues(
                summaryRows.map((row) => row?.total_break_hours),
              ),
            }
          : reportedType === DETAILED_WORKING_HOURS_TYPE
            ? {
                Employees: summaryRows.length,
                "Present Days": summaryRows.reduce(
                  (sum, row) => sum + Number(row?.present_count || 0),
                  0,
                ),
                "Absent Days": summaryRows.reduce(
                  (sum, row) => sum + Number(row?.absent_count || 0),
                  0,
                ),
                "Leave Days": summaryRows.reduce(
                  (sum, row) => sum + Number(row?.leave_count || 0),
                  0,
                ),
                "Total Hours": summaryTotalHours,
              }
            : {}),
      ...(reportedType === BREAK_REPORT_TYPE
        ? {
            Employees: summaryRows.length,
            "Total Break Hours": sumHhMmValues(
              summaryRows.map((row) => row?.total_break_hours),
            ),
          }
        : {}),
    };
  }, [
    departmentName,
    endDate,
    isDetailedDateWiseView,
    reportedType,
    selectedEmployee?.user_name,
    startDate,
    timeCardSummary,
    topbarCompanyName,
    workingHourData,
  ]);

  const handleSummarizedWorkingHoursDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const summaryRow = { rowType: "grand_total" };
      visibleHeaders.forEach((header) => {
        if (header.key === "employee_name") {
          summaryRow[header.key] = "TOTAL";
        } else if (header.type === "time") {
          summaryRow[header.key] = sumHhMmValues(
            rows.map((row) => row?.[header.key]),
          );
        } else if (header.type === "number") {
          summaryRow[header.key] = rows.reduce(
            (sum, row) => sum + Number(row?.[header.key] || 0),
            0,
          );
        }
      });

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Working Hours Summary",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getReportMetadata,
      visibleHeaders,
    ],
  );

  const getSummarizedDayRowCount = useCallback((row) => {
    if (!row || typeof row !== "object") return 0;

    const countKeys = [
      "present_count",
      "absent_count",
      "leave_count",
      "holiday_count",
      "week_off_count",
    ];

    const total = countKeys.reduce(
      (sum, key) => sum + (Number(row?.[key]) || 0),
      0,
    );

    return total > 0 ? total : 1;
  }, []);

  const detailedPdfRowCount = useMemo(
    () =>
      (workingHourData || []).reduce(
        (sum, row) => sum + 1 + getSummarizedDayRowCount(row),
        0,
      ),
    [getSummarizedDayRowCount, workingHourData],
  );

  const breakPdfRowCount = useMemo(
    () =>
      (workingHourData || []).reduce(
        (sum, row) => sum + getSummarizedDayRowCount(row),
        0,
      ),
    [getSummarizedDayRowCount, workingHourData],
  );

  const workingHoursSummaryCards = useMemo(() => {
    const rows = workingHourData || [];

    if (reportedType === SUMMARIZED_WORKING_HOURS_TYPE) {
      return [
        {
          key: "employees",
          title: "Employees",
          value: rows.length,
          icon: Groups2Rounded,
          accent: "#1D4ED8",
          tint: "#EEF4FF",
        },
        {
          key: "working-hours",
          title: "Working Hrs",
          value: sumHhMmValues(rows.map((row) => row?.total_working_hours)),
          icon: ScheduleRounded,
          accent: "#0F766E",
          tint: "#E6F8F4",
        },
        {
          key: "shift-hours",
          title: "Shift Hrs",
          value: sumHhMmValues(rows.map((row) => row?.shift_working_hours)),
          icon: AccessTimeRounded,
          accent: "#EF4444",
          tint: "#FEF2F2",
        },
        {
          key: "ot-hours",
          title: "OT Hrs",
          value: sumHhMmValues(rows.map((row) => row?.ot_hours)),
          icon: MoreTimeRounded,
          accent: "#7C3AED",
          tint: "#F5F3FF",
        },
        {
          key: "break-hours",
          title: "Break Hrs",
          value: sumHhMmValues(rows.map((row) => row?.total_break_hours)),
          icon: FreeBreakfastRounded,
          accent: "#F97316",
          tint: "#FFF7ED",
        },
      ];
    }

    // Detailed's date-wise rows are per-date aggregates from
    // users_working_hours_detailed_summary (total_assigned_present_count,
    // ...), not the per-employee rows (present_count, ...) these cards were
    // built for — reading the employee-wise field names off date rows
    // silently summed to 0 for everything but "Total Hours" (which happens
    // to share its field name across both shapes) and showed the date
    // count instead of the employee count. Summing each date's totals
    // across the visible range gives the same "total person-days" figure
    // the employee-wise cards show; Employees isn't summable the same way
    // (would multiply-count the same people across days), so take the
    // largest single date's headcount instead.
    if (isDetailedDateWiseView) {
      const totalWorkingHours = sumHhMmValues(
        rows.map((row) => row?.total_working_hours || "00:00"),
      );

      return [
        {
          key: "employees",
          title: "Employees",
          value: rows.reduce(
            (max, row) => Math.max(max, Number(row?.total_employee_count) || 0),
            0,
          ),
          icon: Groups2Rounded,
          accent: "#1D4ED8",
          tint: "#EEF4FF",
        },
        {
          key: "present",
          title: "Present Days",
          value: rows.reduce(
            (sum, row) => sum + Number(row?.total_assigned_present_count || 0),
            0,
          ),
          icon: EventAvailableRounded,
          accent: "#0F766E",
          tint: "#E6F8F4",
        },
        {
          key: "absent",
          title: "Absent Days",
          value: rows.reduce(
            (sum, row) => sum + Number(row?.total_assigned_absent_count || 0),
            0,
          ),
          icon: EventBusyRounded,
          accent: "#EF4444",
          tint: "#FEF2F2",
        },
        {
          key: "leave",
          title: "Leave Days",
          value: rows.reduce(
            (sum, row) => sum + Number(row?.total_assigned_leave_count || 0),
            0,
          ),
          icon: BeachAccessRounded,
          accent: "#7C3AED",
          tint: "#F5F3FF",
        },
        {
          key: "hours",
          title: "Total Hours",
          value: totalWorkingHours,
          icon: ScheduleRounded,
          accent: "#F97316",
          tint: "#FFF7ED",
        },
      ];
    }

    const totalWorkingHours = sumHhMmValues(
      rows.map(
        (row) =>
          row?.total_working_hours ||
          row?.total_duty_hours ||
          row?.total_duty_ot_break_hours ||
          "00:00",
      ),
    );

    return [
      {
        key: "employees",
        title: "Employees",
        value: rows.length,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "present",
        title: "Present Days",
        value: rows.reduce(
          (sum, row) => sum + Number(row?.present_count || 0),
          0,
        ),
        icon: EventAvailableRounded,
        accent: "#0F766E",
        tint: "#E6F8F4",
      },
      {
        key: "absent",
        title: "Absent Days",
        value: rows.reduce(
          (sum, row) => sum + Number(row?.absent_count || 0),
          0,
        ),
        icon: EventBusyRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
      {
        key: "leave",
        title: "Leave Days",
        value: rows.reduce(
          (sum, row) => sum + Number(row?.leave_count || 0),
          0,
        ),
        icon: BeachAccessRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "hours",
        title: "Total Hours",
        value: totalWorkingHours,
        icon: ScheduleRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
    ];
  }, [workingHourData, isDetailedDateWiseView, reportedType]);

  const handleChangeRowsPerPage = (event) => {
    setLeaveRowsPerPage(parseInt(event.target.value, 10));
    setLeavePage(0);
  };

  useEffect(() => {
    const recallApi = () => {
      setEditState(false);
      getCheckInCheckOutDetails(personExpandableId);
    };
    if (editState && !openSidePeek) {
      recallApi();
    }
  });

  useEffect(() => {
    const fetchDepartmentData = async () => {
      try {
        const response = await fetch(
          `${domain}get-department/?user_id=${currentUserDetails.id}&company_id=${topbarCompanyId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setDepartmentData([]);
          const departmentDataWithLabel = res.map((obj) => ({
            ...obj,
            label: obj.department_name,
          }));
          const allDepartment = {
            id: defaultAllSelection,
            label: "All",
            departmentname: "All",
          };
          setDepartmentData([allDepartment, ...departmentDataWithLabel]);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast(
          "error",
          "Couldn't load the attendance report. Please try again.",
        );
      }
    };
    fetchDepartmentData();
  }, [currentUserDetails.id, domain, token, topbarCompanyId]);

  useEffect(() => {
    const fetchPunchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          user_id: `${currentUserDetails.id}`,
          shift_id: String(defaultAllSelection),
          department_id: `${departmentId}`,
          start_date: fromDate,
          end_date: toDate,
          company_id: `${topbarCompanyId}`,
        });

        if (reportedType === ADJUSTMENT_REPORT_TYPE) {
          params.set("api_type", "adjustment");
        }

        const response = await fetch(
          `${domain}punchin-details/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setPunchDetailsData(res);
          setmasterPunchDetails(res);
          setLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast(
          "error",
          "Couldn't load the attendance report. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };
    if (fetchPunchDetails && isPunchBasedReport(reportedType)) {
      fetchPunchData();
      setFetchPunchDetails(false);
    }
  }, [
    currentUserDetails.id,
    departmentId,
    dispatch,
    domain,
    fetchPunchDetails,
    fromDate,
    reportedType,
    token,
    toDate,
    topbarCompanyId,
  ]);

  useEffect(() => {
    if (!isPunchBasedReport(reportedType)) {
      return;
    }

    setBreakExpandableDate(0);
    setPersonExpandableId(0);
    // Reuses masterPunchDetails' own reference when there's no active
    // search instead of always calling .filter() (which returns a new array
    // even when nothing is excluded) — that needless reference churn
    // re-triggered a second, unnecessary re-render right after fresh data
    // landed from the fetch handler.
    const filteredResults = globalSearchValue
      ? masterPunchDetails.filter((item) =>
          punchInDetails.some((key) =>
            item[key]
              ?.toString()
              .toLowerCase()
              .includes(globalSearchValue.toString().toLowerCase()),
          ),
        )
      : masterPunchDetails;
    setPunchDetailsData(filteredResults);
    setLeavePage(0);
  }, [globalSearchValue, masterPunchDetails, reportedType]);

  useEffect(() => {
    if (skipInitialCompanyReloadRef.current) {
      skipInitialCompanyReloadRef.current = false;
      return;
    }

    if (topbarCompanyId) {
      setFetchSummarizedReport(true);
      setFetchPunchDetails(true);
      setFetchTimeCardReport(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    const getWorkingHoursDetails = async () => {
      setLoading(true);
      try {
        const breakReportUrl =
          reportedType === BREAK_REPORT_TYPE ||
          reportedType === TIME_CARD_REPORT_TYPE
            ? `${domain}users_working_hours_user_details/?user_id=${currentUserDetails.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&company_id=${topbarCompanyId}`
            : `${domain}summarized-detailed-workinghours/?user_id=${currentUserDetails.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&shift_id=${defaultAllSelection}&company_id=${topbarCompanyId}`;
        const response = await fetch(breakReportUrl, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        });
        const res = await response.json();
        if (response.ok) {
          const responseRows = Array.isArray(res)
            ? res
            : Array.isArray(res?.results)
              ? res.results
              : Array.isArray(res?.data)
                ? res.data
                : [];
          const reportRows =
            reportedType === BREAK_REPORT_TYPE ||
            reportedType === TIME_CARD_REPORT_TYPE
              ? responseRows.map((row) => ({
                  ...row,
                  employee_name:
                    row?.employee_name ||
                    row?.username ||
                    row?.user_name ||
                    "-",
                  total_break_hours:
                    row?.total_break_hours ||
                    sumHhMmValues(
                      (Array.isArray(row?.user_details)
                        ? row.user_details
                        : []
                      ).map((detail) => detail?.break_hours),
                    ),
                  total_working_hours:
                    row?.total_working_hours ||
                    sumHhMmValues(
                      (Array.isArray(row?.user_details)
                        ? row.user_details
                        : []
                      ).map((detail) => detail?.working_hours),
                    ),
                  total_ot_hours:
                    row?.total_ot_hours ||
                    sumHhMmValues(
                      (Array.isArray(row?.user_details)
                        ? row.user_details
                        : []
                      ).map((detail) => detail?.over_time),
                    ),
                }))
              : responseRows;
          setWorkingHoursDetails(reportRows);
          setMasterDataForSearch(reportRows);
          // Also seed the filtered view directly — CustomFilter recomputes
          // workingHourData in its own effect a render later, so without
          // this the table flashes "No Matching Results" for a frame right
          // as loading ends.
          setWorkingHourData(reportRows);
          if (reportedType === TIME_CARD_REPORT_TYPE) {
            setTimeCardApiTotals(
              !Array.isArray(res) && res && typeof res === "object"
                ? {
                    present: res?.total_assigned_present_count,
                    absent: res?.total_assigned_absent_count,
                    holiday: res?.total_assigned_holiday_count,
                    weeklyOff: res?.total_assigned_week_off_count,
                    otHours: res?.total_ot_hours,
                  }
                : null,
            );
          }
          setLoading(false);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast(
          "error",
          "Couldn't load the attendance report. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    };
    if (
      fetchSummarizedReport &&
      !isPunchBasedReport(reportedType) &&
      reportedType !== WORKING_HOURS_REPORT_TYPE &&
      reportedType !== RAW_PUNCH_ENTRY_TYPE
    ) {
      getWorkingHoursDetails();
      setFetchSummarizedReport(false);
    }
  }, [
    currentUserDetails.id,
    departmentId,
    dispatch,
    domain,
    endDate,
    fetchSummarizedReport,
    reportedType,
    startDate,
    token,
    topbarCompanyId,
  ]);

  useEffect(() => {
    if (fetchTimeCardReport && reportedType === TIME_CARD_REPORT_TYPE) {
      setFetchTimeCardReport(false);
    }
  }, [fetchTimeCardReport, reportedType]);

  useEffect(() => {
    const FilterDockets = () => {
      const searchableRows = Array.isArray(masterDataForSearch)
        ? masterDataForSearch
        : [];
      // Reuse searchableRows' own reference when there's no active global
      // search — .filter() always returns a new array even when it
      // excludes nothing, and that new (but content-identical) reference
      // was enough to re-trigger CustomFilter's effect a second time right
      // after the real fetch had already landed correctly, which could
      // flash "No Matching Results" right after the real rows had already
      // shown once (same root cause fixed in Administration/index.js).
      if (!globalSearchValue) return searchableRows;
      const cols = searchableRows[0] && Object.keys(searchableRows[0]);
      if (!cols?.length) return searchableRows;
      return searchableRows.filter((r) =>
        cols.some((c) =>
          r[c]
            ? r[c]
                .toString()
                .toLowerCase()
                .indexOf(globalSearchValue.toLowerCase()) > -1
            : null,
        ),
      );
    };
    setWorkingHoursDetails(FilterDockets());
    setLeavePage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const handleGroupedDetailedDownload = useCallback(
    async (filteredSummarizedData, onProgress) => {
      if (!filteredSummarizedData?.length) return new Map();

      if (isPreparingDetailedData) {
        showToast("info", "Data is preparing, give it a minute");
        return { status: "preparing" };
      }

      const getEmployeeRecord = (detailItem, employeeMap) => {
        const lookupKeys = [
          detailItem?.user_id,
          detailItem?.employee_id,
          detailItem?.user?.id,
          detailItem?.employee?.id,
        ]
          .filter(Boolean)
          .map((value) => `${value}`);

        for (const key of lookupKeys) {
          if (employeeMap.has(key)) return employeeMap.get(key);
        }

        return null;
      };

      const mapDayRow = (detail) => ({
        date: detail?.date || detail?.attendance_date || "-",
        shift: detail?.shift || detail?.shift_name || "-",
        check_in:
          detail?.check_in_time_formatted || detail?.check_in_time || "00:00",
        check_out:
          detail?.check_out_time_formatted || detail?.check_out_time || "00:00",
        break: detail?.break_hours || detail?.break_time || "00:00",
        shift_hours: detail?.shift_hours || "N/A",
        over_time: detail?.over_time || detail?.overtime || "00:00",
        working_hours: detail?.working_hours || "00:00",
        leave_credit_hours:
          detail?.Leave_credit_hours || detail?.leave_credit_hours || "00:00",
        status: detail?.status_label || detail?.status || "00:00",
      });

      const extractPageItems = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.results)) return payload.results;
        if (Array.isArray(payload?.data)) return payload.data;
        if (Array.isArray(payload?.records)) return payload.records;
        if (Array.isArray(payload?.items)) return payload.items;
        if (Array.isArray(payload?.checkin_details))
          return payload.checkin_details;
        return [];
      };

      const employeeMap = new Map(
        filteredSummarizedData.flatMap((item) => {
          const entries = [];
          if (item?.user_id) entries.push([`${item.user_id}`, item]);
          if (item?.employee_id) entries.push([`${item.employee_id}`, item]);
          return entries;
        }),
      );

      const userIds = filteredSummarizedData
        .map((item) => item?.user_id)
        .filter(Boolean);

      if (userIds.length === 0) return new Map();

      const dayRowsByEmployeeKey = new Map();

      const groupKeyForRecord = (record) =>
        `${record?.user_id || record?.employee_id}`;

      const addToGroup = (key, rows) => {
        dayRowsByEmployeeKey.set(key, [
          ...(dayRowsByEmployeeKey.get(key) || []),
          ...rows,
        ]);
      };

      const addPageItems = (items) => {
        items.forEach((item) => {
          if (Array.isArray(item?.checkin_details)) {
            const employeeRecord = getEmployeeRecord(item, employeeMap);
            if (!employeeRecord) return;
            addToGroup(
              groupKeyForRecord(employeeRecord),
              item.checkin_details.map((detail) => mapDayRow(detail)),
            );
            return;
          }

          if (item?.days && typeof item.days === "object") {
            const employeeRecord = getEmployeeRecord(item, employeeMap);
            if (!employeeRecord) return;
            addToGroup(
              groupKeyForRecord(employeeRecord),
              Object.values(item.days).map((detail) => mapDayRow(detail)),
            );
            return;
          }

          const employeeRecord = getEmployeeRecord(item, employeeMap);
          if (!employeeRecord) return;
          addToGroup(groupKeyForRecord(employeeRecord), [mapDayRow(item)]);
        });
      };

      setIsPreparingDetailedData(true);
      if (typeof onProgress === "function") {
        onProgress(0);
      }

      try {
        let currentPage = 1;
        let totalPages = 1;

        while (currentPage <= totalPages) {
          const params = new URLSearchParams({
            user_id: `${currentUserDetails.id}`,
            department_id: `${departmentId}`,
            start_date: startDate,
            end_date: endDate,
            page: `${currentPage}`,
          });

          const response = await fetch(
            `${domain}multiple_individual_working_hours/?${params.toString()}&company_id=${topbarCompanyId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${token}`,
              },
            },
          );

          const res = await response.json();

          if (!response.ok) {
            throw new Error(res?.error || "Unable to fetch detailed report");
          }

          addPageItems(extractPageItems(res));

          const pagination = res?.pagination || {};
          const resolvedTotalPages = Number(pagination.total_pages);
          totalPages =
            Number.isFinite(resolvedTotalPages) && resolvedTotalPages > 0
              ? resolvedTotalPages
              : totalPages;

          if (typeof onProgress === "function") {
            const progressValue = Math.min(
              100,
              Math.round((currentPage / totalPages) * 100),
            );
            onProgress(progressValue);
          }

          if (
            pagination.next_page === null ||
            pagination.next_page === undefined
          ) {
            break;
          }

          currentPage = Number(pagination.next_page);
          if (!Number.isFinite(currentPage) || currentPage <= 0) {
            break;
          }
        }

        if (typeof onProgress === "function") {
          onProgress(100);
        }

        return dayRowsByEmployeeKey;
      } catch (error) {
        showToast("error", error.message);
        return new Map();
      } finally {
        setIsPreparingDetailedData(false);
      }
    },
    [
      currentUserDetails.id,
      domain,
      departmentId,
      endDate,
      isPreparingDetailedData,
      startDate,
      token, topbarCompanyId
    ],
  );

  const handleDetailedWorkingHoursDownload = useCallback(
    async (rows, onProgress, downloadFormat = "excel") => {
      const dayRowsByEmployeeKey = await handleGroupedDetailedDownload(
        rows,
        onProgress,
      );

      if (dayRowsByEmployeeKey?.status === "preparing") {
        return;
      }

      if (!rows?.length) {
        showToast(
          "info",
          "No detailed working hours records are available for export.",
        );
        return;
      }

      const groups = rows.map((row) => {
        const detailRows =
          dayRowsByEmployeeKey.get(`${row?.user_id || row?.employee_id}`) || [];

        return {
          summaryRow: row,
          detailRows,
          totals: {
            over_time: sumHhMmValues(detailRows.map((d) => d.over_time)),
            working_hours: sumHhMmValues(
              detailRows.map((d) => d.working_hours),
            ),
            leave_credit_hours: sumHhMmValues(
              detailRows.map((d) => d.leave_credit_hours),
            ),
          },
        };
      });

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;

      await exportToFormat(
        groups,
        visibleHeaders,
        expandVisibleHeaders,
        "Detailed Working Hours",
        getReportMetadata(),
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      expandVisibleHeaders,
      getReportMetadata,
      handleGroupedDetailedDownload,
      visibleHeaders,
    ],
  );

  // Employee-wise Detailed Working Hours' Download menu was missing the
  // Summarized/Detailed type selector that the date-wise view and Break
  // Report already show (showDownloadOptions + showDownloadTypeSelector) —
  // it only ever exported the full grouped (summary + per-day) file, with
  // no way to download just the employee-level totals. These two adapters
  // re-expose the two download handlers that already exist for this exact
  // data (handleSummarizedWorkingHoursDownload / handleDetailedWorkingHoursDownload,
  // both keyed off the same `visibleHeaders`) under the 6-arg
  // customSummarizedDownloadHandler/customDetailedDownloadHandler signature
  // CustomFilter's type-selector path calls (see handleBreakSummarizedDownload
  // above and handleDetailedDateWiseSummarizedDownload below for the same
  // shape), instead of duplicating either handler's logic.
  const handleDetailedEmployeeWiseSummarizedDownload = useCallback(
    (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat,
      onProgress,
    ) => handleSummarizedWorkingHoursDownload(rows, onProgress, downloadFormat),
    [handleSummarizedWorkingHoursDownload],
  );

  const handleDetailedEmployeeWiseDetailedDownload = useCallback(
    (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat,
      onProgress,
    ) => handleDetailedWorkingHoursDownload(rows, onProgress, downloadFormat),
    [handleDetailedWorkingHoursDownload],
  );

  const getBreakDateWiseDetailValue = useCallback((row, key) => {
    switch (key) {
      case "employee_name":
        return getCellValue(
          row?.employee_name || row?.user_name || row?.username,
          "-",
        );
      case "employee_id":
        return getCellValue(row?.employee_id, "-");
      case "department_name":
        return getCellValue(row?.department_name, "-");
      case "shift":
        return getCellValue(row?.shift || row?.shift_name, "-");
      case "check_in":
        return getCellValue(
          row?.check_in_time_formatted || row?.check_in_time || row?.check_in,
          "-",
        );
      case "check_out":
        return getCellValue(
          row?.check_out_time_formatted ||
            row?.check_out_time ||
            row?.check_out,
          "-",
        );
      case "break":
        return getCellValue(row?.break_hours || row?.break_time, "00:00");
      default:
        return "-";
    }
  }, []);

  const handleBreakSummarizedDownload = useCallback(
    async (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const exportRows = Array.isArray(rows) ? rows : [];

      if (!exportRows.length) {
        showToast("info", "No break report records are available for export.");
        return;
      }

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      if (isBreakDateWiseView) {
        await exportToFormat(
          exportRows.map((row) => ({
            date: row?.date || "-",
            total_break_hours: row?.total_break_hours || "00:00",
          })),
          breakReportDateWiseHeaders,
          "Break",
          {},
          getReportMetadata(),
          [],
          onProgress,
        );
      } else {
        const summaryRow = { rowType: "grand_total" };
        breakVisibleHeaders.forEach((header) => {
          if (header.key === "employee_name") {
            summaryRow[header.key] = "TOTAL";
          } else if (header.key === "total_break_hours") {
            summaryRow[header.key] = sumHhMmValues(
              exportRows.map((row) => row?.total_break_hours),
            );
          }
        });

        await exportToFormat(
          [...exportRows, summaryRow],
          breakVisibleHeaders,
          "Break",
          {},
          getReportMetadata(),
          [],
          onProgress,
        );
      }

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      breakVisibleHeaders,
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getReportMetadata,
      isBreakDateWiseView,
    ],
  );

  const handleGroupedBreakReportDownload = useCallback(
    async (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const getEmbeddedDayRows = (row) => {
        if (Array.isArray(row?.user_details)) return row.user_details;
        if (Array.isArray(row?.checkin_details)) return row.checkin_details;
        if (row?.days && typeof row.days === "object") {
          return Object.values(row.days);
        }
        return [];
      };
      // A day's individual breaks (shown on screen as a further expandable
      // sub-table, see breakReportBreakHeaders) were never carried into the
      // export — only the day's single total "Break" duration was. Expands
      // one day row into one row per break (repeating the day's own fields),
      // or a single "-" row when the day has none, so multi-break days don't
      // collapse into one column that can't represent more than one break.
      const expandBreakDetailRows = (baseFields, breakDetails) => {
        const breaks = Array.isArray(breakDetails) ? breakDetails : [];
        if (!breaks.length) {
          return [
            {
              ...baseFields,
              "Break Start": "-",
              "Break End": "-",
              "Break-Time": "00:00",
            },
          ];
        }
        return breaks.map((breakRecord) => ({
          ...baseFields,
          "Break Start": getBreakStart(breakRecord),
          "Break End": getBreakEnd(breakRecord),
          "Break-Time": getBreakDuration(breakRecord),
        }));
      };
      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;

      if (isBreakDateWiseView) {
        const dateRows = Array.isArray(rows) ? rows : [];
        if (!dateRows.length) {
          showToast(
            "info",
            "No break report records are available for export.",
          );
          return;
        }

        const groups = [];
        for (let index = 0; index < dateRows.length; index += 1) {
          const dateRow = dateRows[index];
          const dateKey = dateRow?.date_key || dateRow?.row_id;
          // Reuses the same normalized date -> details map ensureBreakReportDateDetails
          // and breakDateRows use, instead of re-scanning every employee with a
          // raw (unnormalized) string date comparison once per date — that used to
          // both silently drop rows whose date wasn't already "YYYY-MM-DD" and
          // redo an O(employees) scan per date being exported.
          const detailRows = workingHoursDetailsByDate.get(dateKey) || [];

          groups.push({
            summaryRow: {
              date: getCellValue(dateRow?.date, "-"),
              total_break_hours: dateRow?.total_break_hours || "00:00",
            },
            detailRows: (detailRows || []).flatMap((row) =>
              expandBreakDetailRows(
                {
                  "Employee Name": getBreakDateWiseDetailValue(
                    row,
                    "employee_name",
                  ),
                  "Employee Id": getBreakDateWiseDetailValue(
                    row,
                    "employee_id",
                  ),
                  "Department Name": getBreakDateWiseDetailValue(
                    row,
                    "department_name",
                  ),
                  Shift: getBreakDateWiseDetailValue(row, "shift"),
                  "Check-In": getBreakDateWiseDetailValue(row, "check_in"),
                  "Check-Out": getBreakDateWiseDetailValue(row, "check_out"),
                  Break: getBreakDateWiseDetailValue(row, "break"),
                },
                row?.break_details,
              ),
            ),
          });

          if (typeof onProgress === "function") {
            onProgress(
              Math.min(100, Math.round(((index + 1) / dateRows.length) * 100)),
            );
          }
        }

        if (!groups.some((group) => group.detailRows.length > 0)) {
          showToast(
            "info",
            "No break report records are available for export.",
          );
          return;
        }

        await exportToFormat(
          groups,
          breakReportDateWiseHeaders,
          [...breakReportDateWiseDetailHeaders, ...breakReportBreakHeaders].map(
            (name) => ({ name, key: name }),
          ),
          "Break Detailed",
          getReportMetadata(),
        );
      } else {
        const summaryRows = Array.isArray(rows) ? rows : [];
        if (!summaryRows.length) {
          showToast(
            "info",
            "No break report records are available for export.",
          );
          return;
        }

        const visibleIdentityColumns = breakVisibleHeaders
          .map((header) =>
            BREAK_IDENTITY_EXPORT_COLUMNS.find(
              (column) => column.sourceKey === header.key,
            ),
          )
          .filter(Boolean);
        // visibleIdentityColumns' `key` is the Title Case export header
        // ("Employee Name", "Total Break Hours", ...) while the raw summary
        // row carries snake_case fields (row.employee_name, ...) — passing
        // the row straight through as summaryRow left every identity column
        // blank since exportToFormat reads each cell by header.key. Also
        // reuse getBreakDateWiseDetailValue for the per-day columns instead
        // of reading detailRow?.check_in/break directly — the real fields
        // are check_in_time_formatted/check_in_time and break_hours/
        // break_time, so the direct reads were always falling through to
        // their "-" fallback.
        const groups = summaryRows.map((row) => {
          const identityFields = {};
          visibleIdentityColumns.forEach((column) => {
            identityFields[column.key] = getCellValue(
              row?.[column.sourceKey],
              column.sourceKey === "total_break_hours" ? "00:00" : "-",
            );
          });

          return {
            summaryRow: identityFields,
            detailRows: getEmbeddedDayRows(row).flatMap((detailRow) =>
              expandBreakDetailRows(
                {
                  Date: getCellValue(detailRow?.date, "-"),
                  Shift: getBreakDateWiseDetailValue(detailRow, "shift"),
                  "Check-In": getBreakDateWiseDetailValue(
                    detailRow,
                    "check_in",
                  ),
                  "Check-Out": getBreakDateWiseDetailValue(
                    detailRow,
                    "check_out",
                  ),
                  Break: getBreakDateWiseDetailValue(detailRow, "break"),
                },
                detailRow?.break_details,
              ),
            ),
          };
        });

        if (!groups.some((group) => group.detailRows.length > 0)) {
          showToast(
            "info",
            "No break report records are available for export.",
          );
          return;
        }

        await exportToFormat(
          groups,
          visibleIdentityColumns,
          [...breakReportDateHeaders, ...breakReportBreakHeaders].map(
            (name) => ({ name, key: name }),
          ),
          "Break Detailed",
          getReportMetadata(),
          onProgress,
        );
      }

      if (typeof onProgress === "function") onProgress(100);
    },
    [
      breakVisibleHeaders,
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      getBreakDateWiseDetailValue,
      getReportMetadata,
      isBreakDateWiseView,
      workingHoursDetailsByDate,
    ],
  );

  const getPunchRowsForUser = useCallback(
    async (selectedUserId) => {
      const params = new URLSearchParams({
        user_id: `${currentUserDetails.id}`,
        shift_id: String(defaultAllSelection),
        department_id: `${departmentId}`,
        start_date: startDate,
        end_date: endDate,
        company_id: `${topbarCompanyId}`,
        selected_user_id: `${selectedUserId}`,
        api_type: "adjustment",
      });

      const response = await fetch(
        `${domain}punchin-details/?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );

      const res = await response.json();

      if (response.ok) {
        const selectedUserPunches = Array.isArray(res)
          ? res.find((item) => `${item?.user_id || ""}` === `${selectedUserId}`)
          : res;

        return (
          selectedUserPunches?.punches ||
          selectedUserPunches?.data ||
          selectedUserPunches?.punch_details ||
          []
        );
      }

      if (response.status === 409) {
        sessionHandling();
        return [];
      }

      throw new Error(res?.error || "Unable to fetch adjustment report");
    },
    [
      currentUserDetails.id,
      departmentId,
      domain,
      endDate,
      startDate,
      token,
      topbarCompanyId,
    ],
  );

  // Shared by both Punch Entry and Adjustment Entry — see
  // punchVisibleHeaderKeys' declaration for why one callback covers both.
  const handlePunchVisibleHeadersChange = useCallback((headers) => {
    // Ordered array, not a Set — the saved view's column ORDER matters here
    // too, not just which columns are visible.
    setPunchVisibleHeaderKeys((headers || []).map((header) => header.key));
  }, []);

  const handleAdjustmentReportDownload = useCallback(
    async (rows, onProgress, downloadFormat = "excel") => {
      if (!rows?.length) {
        showToast(
          "info",
          "No adjustment report records are available for export.",
        );
        return;
      }

      try {
        // null means PunchDetails hasn't reported its active saved view yet
        // — fall back to every identity column, in their default order.
        // Otherwise walk the saved view's own key order (not
        // ADJUSTMENT_IDENTITY_EXPORT_COLUMNS' fixed order) so a reordered
        // view actually reorders the export too.
        const visibleIdentityColumns = punchVisibleHeaderKeys
          ? punchVisibleHeaderKeys
              .map((key) =>
                ADJUSTMENT_IDENTITY_EXPORT_COLUMNS.find(
                  (column) => column.sourceKey === key,
                ),
              )
              .filter(Boolean)
          : ADJUSTMENT_IDENTITY_EXPORT_COLUMNS;

        const adjustmentRows = [];
        const rowsWithUser = rows.filter((employee) => employee?.user_id);

        if (typeof onProgress === "function") {
          onProgress(0);
        }

        for (let index = 0; index < rowsWithUser.length; index += 1) {
          const employee = rowsWithUser[index];

          const punchRows = await getPunchRowsForUser(employee.user_id);
          punchRows.filter(isAdjustmentPunchRow).forEach((punchRow) => {
            const row = {};
            visibleIdentityColumns.forEach((column) => {
              row[column.key] =
                adjustmentIdentityValueGetters[column.sourceKey](employee);
            });
            row.Date = getCellValue(punchRow?.date, "-");
            row.Shift = getCellValue(
              punchRow?.shift_name || punchRow?.shift,
              "-",
            );
            row["Original Time"] = getCellValue(punchRow?.punch_time, "-");
            row["Adjusted Time"] = getCellValue(punchRow?.attendance_time, "-");
            row["Modified By"] = getAdjustmentModifiedBy(punchRow);
            row["Modified At"] = getAdjustmentModifiedAt(punchRow);
            adjustmentRows.push(row);
          });

          if (typeof onProgress === "function") {
            onProgress(Math.round(((index + 1) / rowsWithUser.length) * 100));
          }
        }

        if (adjustmentRows.length === 0) {
          showToast(
            "info",
            "No adjustment report records are available for export.",
          );
          return;
        }

        const exportToFormat =
          downloadFormat === "pdf"
            ? downloadPdf
            : downloadFormat === "csv"
              ? downloadCsv
              : downloadExcel;

        // Employees here means the employees whose rows were actually
        // exported (rowsWithUser), matching how the on-screen "Employees"
        // StatCard is derived from the same filtered summary rows —
        // not adjustmentRows.length, which counts one row per adjustment
        // event and would double-count employees with multiple adjustments.
        await exportToFormat(
          adjustmentRows,
          [...visibleIdentityColumns, ...adjustmentReportDetailDownloadHeaders],
          "Adjustment",
          {},
          { ...getReportMetadata(), Employees: rowsWithUser.length },
          [],
        );
        if (typeof onProgress === "function") {
          onProgress(100);
        }
      } catch (error) {
        showToast(
          "error",
          error?.message || "Couldn't download the adjustment report.",
        );
      }
    },
    [
      punchVisibleHeaderKeys,
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getPunchRowsForUser,
      getReportMetadata,
    ],
  );

  const handlePunchReportDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      if (!rows.length) {
        showToast("info", "No punch entry records are available for export.");
        return;
      }

      // null means PunchDetails hasn't reported its active saved view yet
      // — fall back to every column, in their default order. Otherwise walk
      // the saved view's own key order so a reordered view carries through
      // to the export too — same convention as Adjustment Entry above.
      const headers = punchVisibleHeaderKeys
        ? punchVisibleHeaderKeys
            .map((key) =>
              punchDetailsHeader.find((header) => header.key === key),
            )
            .filter(Boolean)
        : punchDetailsHeader;

      const summaryRow = { rowType: "grand_total" };
      headers.forEach((header) => {
        if (header.key === "username") {
          summaryRow[header.key] = "TOTAL";
        } else if (header.type === "number") {
          summaryRow[header.key] = rows.reduce(
            (sum, row) => sum + Number(row?.[header.key] || 0),
            0,
          );
        }
      });

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        headers,
        "Punch Entry",
        {},
        {
          ...getReportMetadata(),
          Employees: rows.length,
          "Total Punches": rows.reduce(
            (sum, row) => sum + Number(row?.punch_in_count || 0),
            0,
          ),
        },
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      downloadCsv,
      downloadExcel,
      downloadPdf,
      getReportMetadata,
      punchVisibleHeaderKeys,
    ],
  );

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setWorkingHourData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setLeavePage(0);
    },
    [page, rowsPerPage, setLeavePage],
  );

  const applyReportDefaultDateRange = useCallback((nextReportType) => {
    const defaultDateRange = getDefaultDateRange(nextReportType);

    setStartDate(defaultDateRange.startApiDate);
    setEndDate(defaultDateRange.endApiDate);
    setFromDate(defaultDateRange.startApiDate);
    setToDate(defaultDateRange.endApiDate);
    setSelectedDateRange(defaultDateRange.displayDateRange);
    setDateRangeLabel(defaultDateRange.label);
  }, []);

  const handleStartDate = useCallback(
    async (startDate, endDate, rangeLabel) => {
      const parsedStartDate = normalizeReportFilterDate(startDate);
      const parsedEndDate = normalizeReportFilterDate(endDate);

      if (!parsedStartDate || !parsedEndDate) {
        return;
      }

      setPersonExpandableId(0);
      resetBreakReportExpansion();
      resetDetailedReportExpansion();
      setStartDate(parsedStartDate.format("YYYY-MM-DD"));
      setEndDate(parsedEndDate.format("YYYY-MM-DD"));
      setFromDate(parsedStartDate.format("YYYY-MM-DD"));
      setToDate(parsedEndDate.format("YYYY-MM-DD"));
      setSelectedDateRange(
        `${parsedStartDate.format("DD-MM-YYYY")} - ${parsedEndDate.format(
          "DD-MM-YYYY",
        )}`,
      );
      setDateRangeLabel(rangeLabel || "Custom Range");
      if (isPunchBasedReport(reportedType)) {
        setFetchPunchDetails(true);
      } else if (reportedType === TIME_CARD_REPORT_TYPE) {
        setFetchSummarizedReport(true);
      } else if (
        reportedType !== WORKING_HOURS_REPORT_TYPE &&
        reportedType !== RAW_PUNCH_ENTRY_TYPE
      ) {
        setFetchSummarizedReport(true);
      }
    },
    [reportedType, resetBreakReportExpansion, resetDetailedReportExpansion],
  );

  const updateReportName = useCallback(
    (event, data) => {
      if (data && data?.label) {
        const nextReportType = normalizeReportType(data.label);
        setReportedType(nextReportType);
        setPersonExpandableId(0);
        setRemoveExpand(true);
        resetBreakReportExpansion();
        resetDetailedReportExpansion();
        dispatch(setFilterApi(true));

        const nextReportUsesCurrentDate = isPunchBasedReport(nextReportType);
        const currentReportUsesCurrentDate = isPunchBasedReport(reportedType);
        const shouldResetDateRange =
          nextReportUsesCurrentDate ||
          (currentReportUsesCurrentDate && !nextReportUsesCurrentDate) ||
          (!nextReportUsesCurrentDate && isCurrentOrFutureDate(endDate));

        if (shouldResetDateRange) {
          applyReportDefaultDateRange(nextReportType);
        }

        if (isPunchBasedReport(nextReportType)) {
          setFetchPunchDetails(true);
        } else if (nextReportType === TIME_CARD_REPORT_TYPE) {
          setFetchSummarizedReport(true);
        } else if (
          nextReportType !== WORKING_HOURS_REPORT_TYPE &&
          nextReportType !== RAW_PUNCH_ENTRY_TYPE
        ) {
          setFetchSummarizedReport(true);
        }
      }
    },
    [
      applyReportDefaultDateRange,
      dispatch,
      endDate,
      reportedType,
      resetBreakReportExpansion,
      resetDetailedReportExpansion,
    ],
  );

  const updateDepartmentName = useCallback(
    (event, data) => {
      if (data && data.label) {
        setDepartmentId(data.id);
        setDepartmentName(
          data.department_name || data.departmentname || data.label,
        );
        setShiftCatagoryName(data.shift_category_name);
        localStorage.setItem("approveLeaveDepartmentId", data.id);
        localStorage.setItem("approveLeaveDepartmentName", data.label);
        dispatch(setDepartmentName(data.label));
        dispatch(setDepartmentId(data.id));
        lastEmployeePrefillDepartmentRef.current = null;
        setMembersData([]);
        setSelectedEmployee({
          id: defaultAllSelection,
          label: "All",
          user_id: defaultAllSelection,
          user_name: "All",
          employee_id: "",
        });
        setPersonExpandableId(0);
        setRemoveExpand(true);
        resetBreakReportExpansion();
        resetDetailedReportExpansion();
        if (isPunchBasedReport(reportedType)) {
          setFetchPunchDetails(true);
        } else if (reportedType === TIME_CARD_REPORT_TYPE) {
          setFetchSummarizedReport(true);
        } else if (reportedType !== WORKING_HOURS_REPORT_TYPE) {
          setFetchSummarizedReport(true);
        }
      }
    },
    [
      dispatch,
      reportedType,
      resetBreakReportExpansion,
      resetDetailedReportExpansion,
    ],
  );

  const updateEmployee = useCallback((event, data) => {
    if (data) {
      setSelectedEmployee(data);
    }
    setLeavePage(0);
  }, []);

  const headerFilterControls = useMemo(() => {
    if (reportedType === WORKING_HOURS_REPORT_TYPE) return null;

    const selectedDepartmentOption =
      departmentData.find((option) => option?.id === departmentId) ||
      departmentData.find((option) => option?.label === departmentName) ||
      null;
    const selectedReportTypeOption =
      reportTypeOptions.find(
        (option) =>
          option?.label === (selectedReportType || reportedType) ||
          option?.value === (selectedReportType || reportedType),
      ) || null;

    return (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={shouldIncludeCurrentDate}
          includeCurrentDate={shouldIncludeCurrentDate}
          allowFutureDate={false}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentData}
          selectedValue={selectedDepartmentOption}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        {reportedType === TIME_CARD_REPORT_TYPE ? (
          <CompactHeaderSelectFilter
            dataList={employeeOptions}
            showEmployeeDetails
            selectedValue={selectedEmployee}
            updateDataFunction={updateEmployee}
            Icons={PersonOutlineIcon}
            staticLabel="Employee"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 220 },
            }}
          />
        ) : null}
        {reportTypeOptions?.length ? (
          <CompactHeaderSelectFilter
            dataList={reportTypeOptions}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange || updateReportName}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
        {isPunchBasedReport(reportedType) ? (
          <CompactHeaderSelectFilter
            dataList={PUNCH_VIEW_MODE_OPTIONS}
            selectedValue={punchViewMode}
            updateDataFunction={updatePunchViewMode}
            Icons={PersonOutlineIcon}
            staticLabel="View"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          />
        ) : null}
        {reportedType === BREAK_REPORT_TYPE ? (
          <CompactHeaderSelectFilter
            dataList={BREAK_VIEW_MODE_OPTIONS}
            selectedValue={breakViewMode}
            updateDataFunction={updateBreakViewMode}
            Icons={PersonOutlineIcon}
            staticLabel="View"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          />
        ) : null}
        {reportedType === TIME_CARD_REPORT_TYPE ? (
          <CompactHeaderSelectFilter
            dataList={TIME_CARD_VIEW_MODE_OPTIONS}
            selectedValue={timeCardViewMode}
            updateDataFunction={updateTimeCardViewMode}
            Icons={PersonOutlineIcon}
            staticLabel="View"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          />
        ) : null}
        {reportedType === DETAILED_WORKING_HOURS_TYPE ? (
          <CompactHeaderSelectFilter
            dataList={DETAILED_VIEW_MODE_OPTIONS}
            selectedValue={detailedViewMode}
            updateDataFunction={updateDetailedViewMode}
            Icons={PersonOutlineIcon}
            staticLabel="View"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 200 },
            }}
          />
        ) : null}
      </Mui.Stack>
    );
  }, [
    breakViewMode,
    dateRangeLabel,
    departmentId,
    departmentData,
    departmentName,
    employeeOptions,
    endDate,
    handleStartDate,
    onReportTypeChange,
    reportTypeOptions,
    reportedType,
    punchViewMode,
    timeCardViewMode,
    detailedViewMode,
    selectedEmployee,
    selectedReportType,
    shouldIncludeCurrentDate,
    startDate,
    updateBreakViewMode,
    updateDepartmentName,
    updateDetailedViewMode,
    updateEmployee,
    updatePunchViewMode,
    updateTimeCardViewMode,
    updateReportName,
  ]);

  // Filters render inline in the page body now (matching Employee
  // Attendance's own row below the title), not floated beside the title —
  // so nothing gets pushed up into the shared page header any more.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  const getCheckInCheckOutDetails = useCallback(
    async (userid) => {
      setUserLoader(true);
      try {
        const response = await fetch(
          `${domain}individual_details_working_hours/?user_id=${userid}&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setUserDetails(res);
          setDetailedDetailsByUserId((prev) => ({
            ...prev,
            [userid]: res,
          }));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        setUserDetails([]);
        showToast(
          "error",
          "Couldn't load the attendance report. Please try again.",
        );
      } finally {
        setUserLoader(false);
      }
    },
    [domain, endDate, startDate, token, topbarCompanyId],
  );

  useEffect(() => {
    if (!isDetailedDateWiseView) return undefined;
    let cancelled = false;

    const fetchDetailedDateSummary = async () => {
      setDetailedDateSummaryLoading(true);
      try {
        const response = await fetch(
          `${domain}users_working_hours_detailed_summary/?user_id=${currentUserDetails.id}&start_date=${startDate}&end_date=${endDate}&department_id=${departmentId}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (cancelled) return;
        if (response.ok) {
          const rows = Object.keys(res || {})
            .sort()
            .map((dateKey) => ({ date_key: dateKey, ...res[dateKey] }));
          setDetailedDateSummaryRows(rows);
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        if (cancelled) return;
        console.error("[Index] operation failed:", error);
        setDetailedDateSummaryRows([]);
        showToast(
          "error",
          "Couldn't load the date-wise summary. Please try again.",
        );
      } finally {
        if (!cancelled) setDetailedDateSummaryLoading(false);
      }
    };

    fetchDetailedDateSummary();
    setExpandedDetailedDates([]);
    setDetailedDateExpandDataByDate({});
    setDetailedDateExpandSearchByKey({});
    return () => {
      cancelled = true;
    };
  }, [
    isDetailedDateWiseView,
    startDate,
    endDate,
    departmentId,
    domain,
    currentUserDetails.id,
    token, topbarCompanyId
  ]);

  const fetchDetailedDateExpandPage = useCallback(
    async (dateKey, pageNumber) => {
      setDetailedDateExpandDataByDate((prev) => ({
        ...prev,
        [dateKey]: { ...(prev[dateKey] || {}), loading: true },
      }));
      try {
        const response = await fetch(
          `${domain}multiple_individual_working_hours/?user_id=${currentUserDetails.id}&department_id=${departmentId}&start_date=${dateKey}&end_date=${dateKey}&page=${pageNumber}&page_size=${BREAK_DATE_DETAIL_PAGE_SIZE}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setDetailedDateExpandDataByDate((prev) => ({
            ...prev,
            [dateKey]: {
              results: Array.isArray(res?.results) ? res.results : [],
              pagination: res?.pagination || null,
              loading: false,
            },
          }));
        } else if (response.status === 409) {
          sessionHandling();
          setDetailedDateExpandDataByDate((prev) => ({
            ...prev,
            [dateKey]: { ...(prev[dateKey] || {}), loading: false },
          }));
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Index] operation failed:", error);
        showToast(
          "error",
          "Couldn't load employee details for this date. Please try again.",
        );
        setDetailedDateExpandDataByDate((prev) => ({
          ...prev,
          [dateKey]: { ...(prev[dateKey] || {}), loading: false },
        }));
      }
    },
    [domain, departmentId, currentUserDetails.id, token, topbarCompanyId],
  );

  const handleToggleDetailedDate = useCallback(
    (dateKey) => {
      setExpandedDetailedDates((prev) => {
        if (prev.includes(dateKey)) {
          setDetailedDateExpandSearchByKey((searchPrev) => {
            if (!(dateKey in searchPrev)) return searchPrev;
            const next = { ...searchPrev };
            delete next[dateKey];
            return next;
          });
          return prev.filter((key) => key !== dateKey);
        }
        if (!detailedDateExpandDataByDate[dateKey]) {
          fetchDetailedDateExpandPage(dateKey, 1);
        }
        return [...prev, dateKey];
      });
    },
    [detailedDateExpandDataByDate, fetchDetailedDateExpandPage],
  );

  const handleDetailedDateExpandPageChange = useCallback(
    (dateKey, nextPageZeroIndexed) => {
      fetchDetailedDateExpandPage(dateKey, nextPageZeroIndexed + 1);
    },
    [fetchDetailedDateExpandPage],
  );

  const updateDetailedDateExpandMode = useCallback((event, data) => {
    const nextMode = data?.id;
    if (!nextMode) return;

    setDetailedDateExpandMode(nextMode);
    if (nextMode === "collapse-all") {
      setExpandedDetailedDates([]);
      setDetailedDateExpandSearchByKey({});
    }
  }, []);

  // Only expands + fetches the currently visible outer page's dates (never
  // more than DATE_WISE_ROWS_PER_PAGE_OPTIONS-many at once) — each date is
  // its own server-paginated fetch, so "Expand All" here can't reintroduce
  // the earlier thousands-of-rows-at-once hang.
  useEffect(() => {
    if (detailedDateExpandMode !== "expand-all" || !isDetailedDateWiseView) {
      return;
    }

    const visibleDateKeys = (
      Array.isArray(workingHourData) ? workingHourData : []
    )
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map((row) => row?.date_key)
      .filter(Boolean);

    setExpandedDetailedDates((prev) =>
      Array.from(new Set([...prev, ...visibleDateKeys])),
    );
    visibleDateKeys.forEach((dateKey) => {
      if (!detailedDateExpandDataByDate[dateKey]) {
        fetchDetailedDateExpandPage(dateKey, 1);
      }
    });
  }, [
    detailedDateExpandMode,
    isDetailedDateWiseView,
    workingHourData,
    page,
    rowsPerPage,
    detailedDateExpandDataByDate,
    fetchDetailedDateExpandPage,
  ]);

  const detailedDateExpandControl = useMemo(
    () =>
      isDetailedDateWiseView ? (
        <CompactHeaderSelectFilter
          dataList={DETAILED_DATE_EXPAND_OPTIONS}
          selectedValue={
            DETAILED_DATE_EXPAND_OPTIONS.find(
              (option) => option.id === detailedDateExpandMode,
            ) || DETAILED_DATE_EXPAND_OPTIONS[0]
          }
          updateDataFunction={updateDetailedDateExpandMode}
          staticLabel=""
          Icons={MoreTimeRounded}
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 140 },
            maxWidth: { xs: "100%", sm: 140 },
          }}
          buttonSx={{
            minHeight: 40,
            height: 40,
            px: 1,
            borderRadius: 1.5,
            backgroundColor: designColors.white,
            border: `1px solid ${designColors.slate200}`,
            boxShadow: "none",
            color: designColors.slate700,
            "&:hover": {
              backgroundColor: designColors.slateBg,
              border: `1px solid ${designColors.slate300}`,
              boxShadow: "none",
            },
          }}
        />
      ) : null,
    [
      detailedDateExpandMode,
      isDetailedDateWiseView,
      updateDetailedDateExpandMode,
    ],
  );

  const handleDetailedDateWiseSummarizedDownload = useCallback(
    async (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const exportRows = Array.isArray(rows) ? rows : [];

      if (!exportRows.length) {
        showToast("info", "No date-wise records are available for export.");
        return;
      }

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        exportRows,
        detailedDateWiseHeaders,
        "Detailed Working Hours",
        {},
        getReportMetadata(),
        [],
        onProgress,
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [downloadCsv, downloadExcel, downloadPdf, getReportMetadata],
  );

  const handleDetailedDateWiseDetailedDownload = useCallback(
    async (
      rows,
      _metadataOverride,
      _filterValues,
      _sortRules,
      downloadFormat = "excel",
      onProgress,
    ) => {
      const dateRows = Array.isArray(rows) ? rows : [];
      if (!dateRows.length) {
        showToast("info", "No date-wise records are available for export.");
        return;
      }

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadGroupedPdf
          : downloadFormat === "csv"
            ? downloadGroupedCsv
            : downloadGroupedExcel;

      // Unlike Break's grouped export (everything already loaded client
      // side), this view deliberately doesn't bulk-load every employee's
      // day records up front — so building the detailed export means
      // walking multiple_individual_working_hours per date, page by page,
      // during the export itself.
      const groups = [];
      for (let index = 0; index < dateRows.length; index += 1) {
        const dateRow = dateRows[index];
        const dateKey = dateRow?.date_key;
        const employees = [];
        let currentPage = 1;
        let totalPages = 1;

        do {
          try {
            const response = await fetch(
              `${domain}multiple_individual_working_hours/?user_id=${currentUserDetails.id}&department_id=${departmentId}&start_date=${dateKey}&end_date=${dateKey}&page=${currentPage}&page_size=${BREAK_DATE_DETAIL_PAGE_SIZE}&company_id=${topbarCompanyId}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `token ${token}`,
                },
              },
            );
            const res = await response.json();
            if (!response.ok) {
              if (response.status === 409) sessionHandling();
              break;
            }
            (Array.isArray(res?.results) ? res.results : []).forEach(
              (employee) => employees.push(employee),
            );
            totalPages = res?.pagination?.total_pages || 1;
          } catch (error) {
            console.error("[Index] operation failed:", error);
            break;
          }
          currentPage += 1;
        } while (currentPage <= totalPages);

        groups.push({
          summaryRow: detailedDateWiseHeaders.reduce((acc, header) => {
            acc[header.key] = getCellValue(dateRow?.[header.key], "-");
            return acc;
          }, {}),
          detailRows: employees.map((employee) => {
            const day = employee?.days?.[dateKey] || {};
            return {
              user_name: getCellValue(employee?.user_name, "-"),
              employee_id: getCellValue(employee?.employee_id, "-"),
              department_name: getCellValue(employee?.department_name, "-"),
              shift: getCellValue(day?.shift, "-"),
              check_in: getCellValue(day?.check_in_time_formatted, "-"),
              check_out: getCellValue(day?.check_out_time_formatted, "-"),
              break: getCellValue(day?.break_hours, "-"),
              shift_hours: getCellValue(day?.shift_hours, "-"),
              over_time: getCellValue(day?.over_time, "-"),
              working_hours: getCellValue(day?.working_hours, "-"),
              leave_credit_hours: getCellValue(day?.Leave_credit_hours, "-"),
              status: getCellValue(day?.status_label, "-"),
            };
          }),
        });

        if (typeof onProgress === "function") {
          onProgress(
            Math.min(100, Math.round(((index + 1) / dateRows.length) * 100)),
          );
        }
      }

      if (!groups.some((group) => group.detailRows.length > 0)) {
        showToast("info", "No employee records are available for export.");
        return;
      }

      const detailExportHeaders = [
        ...DETAILED_DATE_EXPAND_IDENTITY_HEADERS,
        ...workingHoursHeader.filter((header) => header.key !== "date"),
      ];

      await exportToFormat(
        groups,
        detailedDateWiseHeaders,
        detailExportHeaders,
        "Detailed Working Hours - Detailed",
        getReportMetadata(),
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [
      domain,
      departmentId,
      currentUserDetails.id,
      token,
      downloadGroupedCsv,
      downloadGroupedExcel,
      downloadGroupedPdf,
      getReportMetadata, topbarCompanyId
    ],
  );

  const renderBreakIdentityCell = (key, element) => {
    switch (key) {
      case "date":
        return getCellValue(element?.date, "-");
      case "employee_name":
        return (
          <Mui.Stack direction="row" alignItems="center">
            <Mui.Avatar
              sx={{ width: 23, height: 23 }}
              alt={element?.employee_name?.charAt(0).toUpperCase()}
              src={element?.image_url ?? optionalAvathar}
            />
            <Mui.Typography sx={{ paddingLeft: 1 }}>
              {getCellValue(element?.employee_name)}
            </Mui.Typography>
          </Mui.Stack>
        );
      case "employee_id":
        return getCellValue(element?.employee_id);
      case "department_name":
        return getCellValue(element?.department_name);
      case "total_break_hours":
        return getCellValue(element?.total_break_hours, "00:00");
      default:
        return null;
    }
  };

  const renderDetailedDateWiseRow = (row) => {
    const dateKey = row.date_key;
    const isExpanded = expandedDetailedDates.includes(dateKey);
    const expandData = detailedDateExpandDataByDate[dateKey];
    const displayDate = moment(
      dateKey,
      ["YYYY-MM-DD", "DD-MM-YYYY", moment.ISO_8601],
      true,
    ).isValid()
      ? moment(dateKey).format("DD/MM/YYYY ddd")
      : dateKey;
    const pagination = expandData?.pagination;
    const search = detailedDateExpandSearchByKey[dateKey] || "";
    const normalizedSearch = search.trim().toLowerCase();
    const allResults = expandData?.results || [];
    const filteredResults = normalizedSearch
      ? allResults.filter((employee) => {
          const name = `${employee?.user_name || ""}`.toLowerCase();
          const employeeId = `${employee?.employee_id || ""}`.toLowerCase();
          return (
            name.includes(normalizedSearch) ||
            employeeId.includes(normalizedSearch)
          );
        })
      : allResults;

    return (
      <React.Fragment key={dateKey}>
        <Mui.TableRow sx={tableRowSx} hover>
          <Mui.TableCell
            sx={{ width: 48, cursor: "pointer" }}
            onClick={() => handleToggleDetailedDate(dateKey)}
          >
            <ArrowRightIcon
              sx={{
                color: designColors.slate500,
                cursor: "pointer",
                transition: "transform 0.2s ease",
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          </Mui.TableCell>
          {detailedDateWiseHeaders.map((header) => (
            <Mui.TableCell key={header.key}>
              {header.key === "date_key"
                ? displayDate
                : getCellValue(row?.[header.key], "-")}
            </Mui.TableCell>
          ))}
        </Mui.TableRow>
        {isExpanded ? (
          <Mui.TableRow>
            <Mui.TableCell
              colSpan={detailedDateWiseHeaders.length + 1}
              sx={{ p: 0, border: "none" }}
            >
              {expandData?.loading ? (
                <Mui.Box
                  sx={{ py: 3, display: "flex", justifyContent: "center" }}
                >
                  <Mui.CircularProgress size={22} />
                </Mui.Box>
              ) : !allResults.length ? (
                <EmptyState
                  title="No employee records"
                  subtitle="No employee data was found for this date."
                  size={80}
                  sx={{ py: 2 }}
                />
              ) : (
                <>
                  <Mui.Box
                    sx={{
                      p: 1.25,
                      pb: 0.75,
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Mui.TextField
                      size="small"
                      sx={{ width: 280, maxWidth: "100%" }}
                      placeholder="Search by name, id..."
                      value={search}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        setDetailedDateExpandSearchByKey((prev) => ({
                          ...prev,
                          [dateKey]: nextValue,
                        }));
                      }}
                      InputProps={{
                        startAdornment: (
                          <Mui.InputAdornment position="start">
                            <SearchRounded
                              sx={{
                                fontSize: 18,
                                color: designColors.slate500,
                              }}
                            />
                          </Mui.InputAdornment>
                        ),
                      }}
                    />
                  </Mui.Box>
                  {filteredResults.length === 0 ? (
                    <EmptyState
                      title="No Matching Results"
                      subtitle="No employees match your search."
                      size={64}
                      sx={{ py: 2 }}
                    />
                  ) : (
                    <ApproveTable size="small">
                      <Mui.TableHead>
                        <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                          {DETAILED_DATE_EXPAND_IDENTITY_HEADERS.map(
                            (header) => (
                              <Mui.TableCell
                                key={header.key}
                                sx={approveTableHeadCell}
                              >
                                {header.name}
                              </Mui.TableCell>
                            ),
                          )}
                          {workingHoursHeader
                            .filter((header) => header.key !== "date")
                            .map((header) => (
                              <Mui.TableCell
                                key={header.key}
                                sx={approveTableHeadCell}
                              >
                                {header.name}
                              </Mui.TableCell>
                            ))}
                        </Mui.TableRow>
                      </Mui.TableHead>
                      <Mui.TableBody>
                        {filteredResults.map((employee) => {
                          const day = employee?.days?.[dateKey] || {};
                          return (
                            <Mui.TableRow
                              key={employee.user_id}
                              sx={approveTableBodyCell}
                            >
                              <Mui.TableCell>
                                {getCellValue(employee?.user_name, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(employee?.employee_id, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(employee?.department_name, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.shift, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(
                                  day?.check_in_time_formatted,
                                  "-",
                                )}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(
                                  day?.check_out_time_formatted,
                                  "-",
                                )}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.break_hours, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.shift_hours, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.over_time, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.working_hours, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {getCellValue(day?.Leave_credit_hours, "-")}
                              </Mui.TableCell>
                              <Mui.TableCell>
                                {/* Short status code (P/A/L/...) + full label
                                  on hover — matches this same report's
                                  employee-wise expand panel, not the full
                                  "Present"/"Absent" text a Chip would show. */}
                                <Mui.Tooltip
                                  arrow
                                  slotProps={tooltipStyleProps}
                                  placement="top"
                                  title={getStatusTooltip(day?.status)}
                                >
                                  <Mui.Typography
                                    sx={{
                                      display: "inline-block",
                                      width: 28,
                                      height: 28,
                                      lineHeight: "28px",
                                      textAlign: "center",
                                      borderRadius: "6px",
                                      fontSize: 12.5,
                                      fontWeight: 700,
                                      color: designColors.white,
                                      backgroundColor:
                                        day?.color || designColors.slate500,
                                      cursor: "default",
                                    }}
                                  >
                                    {getCellValue(day?.status, "-")}
                                  </Mui.Typography>
                                </Mui.Tooltip>
                              </Mui.TableCell>
                            </Mui.TableRow>
                          );
                        })}
                      </Mui.TableBody>
                    </ApproveTable>
                  )}
                  {pagination
                    ? compactRowPager({
                        page: (pagination.current_page || 1) - 1,
                        pageSize:
                          pagination.page_size || BREAK_DATE_DETAIL_PAGE_SIZE,
                        total: pagination.total_users || 0,
                        itemLabel: "employees",
                        onPageChange: (nextPage) =>
                          handleDetailedDateExpandPageChange(dateKey, nextPage),
                      })
                    : null}
                </>
              )}
            </Mui.TableCell>
          </Mui.TableRow>
        ) : null}
      </React.Fragment>
    );
  };

  const renderDetailedDateWiseView = () => {
    const filteredDateRows = Array.isArray(workingHourData)
      ? workingHourData
      : [];
    const paginatedDateRows = filteredDateRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    return (
      <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            key={`detailed-date-filter-${startDate}-${endDate}-${departmentId}`}
            title={DETAILED_WORKING_HOURS_TYPE}
            filterHeaders={detailedDateWiseHeaders}
            data={detailedDateSummaryRows}
            filteredData={workingHourData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            isAttendanceScreenReport={true}
            showDownloadOptions={true}
            showDownloadTypeSelector={true}
            downloadOptionStates={{
              summarized: {
                disabled: filteredDateRows.length === 0,
                reason: "No date-wise summary data is available to export.",
              },
              detailed: {
                disabled: filteredDateRows.length === 0,
                reason: "No date-wise detailed data is available to export.",
              },
            }}
            downloadDisabledReason="No date-wise data is available to export."
            includeMetadata={true}
            metadata={getReportMetadata()}
            customSummarizedDownloadHandler={
              handleDetailedDateWiseSummarizedDownload
            }
            customDetailedDownloadHandler={
              handleDetailedDateWiseDetailedDownload
            }
            downloadFormats={["excel", "pdf", "csv"]}
            showtitle_count={true}
            hideCount={true}
            labeledToolbar={true}
            searchPlaceholder="Search by date..."
            newButtonComponent={detailedDateExpandControl}
          />
        </Box>
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            sx={{
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              // Must stay "visible" on both axes, not "auto" — "auto" (MUI's
              // own default even when unset) turns this box into its own
              // scroll container and hijacks the sticky header's
              // containing block away from .main-content, the page's real
              // single scroll region. Same fix already applied to every
              // other page-level table in this file (see the identical
              // comment on the employee-wise table's own TableContainer).
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
              borderLeft: `1px solid ${designColors.slate200}`,
              borderRight: `1px solid ${designColors.slate200}`,
              borderBottom: `1px solid ${designColors.slate200}`,
            }}
          >
            <ApproveTable stickyHeader sx={{ width: "100%" }}>
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET,
                      width: 48,
                      borderTopLeftRadius: 0,
                    }}
                  />
                  {detailedDateWiseHeaders.map((header, headerIndex) => (
                    <Mui.TableCell
                      key={header.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET,
                        ...(headerIndex === detailedDateWiseHeaders.length - 1
                          ? { borderTopRightRadius: 0 }
                          : {}),
                      }}
                    >
                      {header.name}
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {detailedDateSummaryLoading ? (
                  <Mui.TableRow>
                    <Mui.TableCell
                      colSpan={detailedDateWiseHeaders.length + 1}
                      sx={{ border: "none" }}
                    >
                      <Mui.Box
                        sx={{
                          py: 4,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Mui.CircularProgress size={24} />
                      </Mui.Box>
                    </Mui.TableCell>
                  </Mui.TableRow>
                ) : paginatedDateRows.length === 0 ? (
                  <TableEmptyState
                    dataLength={detailedDateSummaryRows}
                    colSpan={detailedDateWiseHeaders.length + 1}
                  />
                ) : (
                  paginatedDateRows.map((row) => renderDetailedDateWiseRow(row))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
        </Box>
        <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
          {paginationFooter({
            page,
            rowsPerPage,
            total: filteredDateRows.length,
            itemLabel: "dates",
            onPageChange: setLeavePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
            rowsPerPageOptions: DATE_WISE_ROWS_PER_PAGE_OPTIONS,
          })}
        </Mui.Box>
      </Box>
    );
  };

  const renderBreakReport = () => (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={currentUserDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              "@media (max-width:599.95px)": {
                "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {breakSummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>

          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              key={`break-filter-${breakViewMode?.id || "employee-wise"}-${startDate}-${endDate}`}
              title={BREAK_REPORT_TYPE}
              titleOverride={breakTitleControl}
              filterHeaders={breakActiveHeaders}
              data={breakSourceRows}
              filteredData={workingHourData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              isAttendanceScreenReport={true}
              showDownloadOptions={true}
              showDownloadTypeSelector={true}
              downloadOptionStates={{
                summarized: {
                  disabled:
                    !Array.isArray(workingHourData) ||
                    workingHourData.length === 0,
                  reason:
                    "No summarized break report data is available to export.",
                },
                detailed: {
                  disabled:
                    !Array.isArray(workingHourData) ||
                    workingHourData.length === 0,
                  reason:
                    "No detailed break report data is available to export.",
                },
              }}
              downloadDisabledReason="No break report data is available to export."
              includeMetadata={true}
              metadata={getReportMetadata()}
              customSummarizedDownloadHandler={handleBreakSummarizedDownload}
              customDetailedDownloadHandler={handleGroupedBreakReportDownload}
              downloadFormats={["excel", "pdf", "csv"]}
              pdfRowCountOverride={breakPdfRowCount}
              showtitle_count={true}
              hideCount={true}
              labeledToolbar={true}
              searchPlaceholder={
                isBreakDateWiseView
                  ? "Search by date..."
                  : "Search by employee, department..."
              }
              newButtonComponent={breakExpandControl}
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.Box>
              <Mui.TableContainer
                sx={{
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: { xs: "auto", sm: "visible" },
                  overflowY: { xs: "auto", sm: "visible" },
                  pb: footerHeight ? `${footerHeight}px` : 0,
                  borderLeft: `1px solid ${designColors.slate200}`,
                  borderRight: `1px solid ${designColors.slate200}`,
                  borderBottom: `1px solid ${designColors.slate200}`,
                }}
              >
                <ApproveTable stickyHeader sx={{ width: "100%" }}>
                  <Mui.TableHead sx={designShell.tableStickyHead}>
                    <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                      <Mui.TableCell
                        sx={{
                          ...approveTableHeadCell,
                          top: APP_BAR_OFFSET + customFilterHeight,
                          width: 56,
                          borderTopLeftRadius: 0,
                        }}
                      />
                      {breakActiveHeaders.map((header, headerIndex) => (
                        <Mui.TableCell
                          key={header.key}
                          sx={{
                            ...approveTableHeadCell,
                            top: APP_BAR_OFFSET + customFilterHeight,
                            ...(headerIndex === breakActiveHeaders.length - 1
                              ? { borderTopRightRadius: 0 }
                              : {}),
                          }}
                        >
                          {header.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {workingHourData.length === 0 && loading === false ? (
                      <TableEmptyState dataLength={workingHoursDetails} />
                    ) : (
                      visibleBreakRows.map((element) => {
                        if (isBreakDateWiseView) {
                          const rowKey = element?.date_key || element?.row_id;
                          const isExpanded =
                            breakExpandMode === "expand-all" ||
                            breakExpandedSummaryKeys.includes(rowKey);
                          const breakDateDetails =
                            breakDateDetailsByDateKey[rowKey] || [];
                          const breakDateDetailSearch =
                            breakDateDetailSearchByKey[rowKey] || "";
                          const normalizedBreakDateDetailSearch =
                            breakDateDetailSearch.trim().toLowerCase();
                          const filteredBreakDateDetails =
                            normalizedBreakDateDetailSearch
                              ? breakDateDetails.filter((detail) => {
                                  const name = `${
                                    detail?.employee_name ||
                                    detail?.user_name ||
                                    detail?.username ||
                                    ""
                                  }`.toLowerCase();
                                  const employeeId = `${
                                    detail?.employee_id || ""
                                  }`.toLowerCase();
                                  return (
                                    name.includes(
                                      normalizedBreakDateDetailSearch,
                                    ) ||
                                    employeeId.includes(
                                      normalizedBreakDateDetailSearch,
                                    )
                                  );
                                })
                              : breakDateDetails;
                          const breakDateDetailsPage =
                            breakDateDetailPageByKey[rowKey] || 0;
                          const visibleBreakDateDetails =
                            filteredBreakDateDetails.slice(
                              breakDateDetailsPage *
                                BREAK_DATE_DETAIL_PAGE_SIZE,
                              breakDateDetailsPage *
                                BREAK_DATE_DETAIL_PAGE_SIZE +
                                BREAK_DATE_DETAIL_PAGE_SIZE,
                            );
                          const isBreakDateLoading =
                            breakDateLoadingKeys.includes(rowKey);

                          return (
                            <React.Fragment key={rowKey}>
                              <Mui.TableRow
                                hover
                                sx={{
                                  ...designShell.tableRowHover,
                                  "&:last-child td": { borderBottom: "none" },
                                  "& td": {
                                    backgroundColor: designColors.white,
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  sx={approveTableBodyCell}
                                  onClick={async () => {
                                    if (isExpanded) {
                                      setBreakExpandMode("collapse-all");
                                      setBreakExpandedSummaryKeys((prev) =>
                                        prev.filter((key) => key !== rowKey),
                                      );
                                      setBreakExpandedDateKeys((prev) =>
                                        prev.filter(
                                          (key) =>
                                            !key.startsWith(`${rowKey}-`),
                                        ),
                                      );
                                      return;
                                    }

                                    setBreakExpandMode("collapse-all");
                                    setBreakExpandedSummaryKeys((prev) =>
                                      prev.includes(rowKey)
                                        ? prev
                                        : [...prev, rowKey],
                                    );
                                    await ensureBreakReportDateDetails(rowKey);
                                  }}
                                >
                                  <ArrowRightIcon
                                    sx={{
                                      color: designColors.slate500,
                                      cursor: "pointer",
                                      transition: "transform 0.2s ease",
                                      transform: isExpanded
                                        ? "rotate(90deg)"
                                        : "rotate(0deg)",
                                    }}
                                  />
                                </Mui.TableCell>
                                {breakActiveHeaders.map((header) => (
                                  <Mui.TableCell
                                    key={header.key}
                                    sx={approveTableBodyCell}
                                  >
                                    {renderBreakIdentityCell(
                                      header.key,
                                      element,
                                    )}
                                  </Mui.TableCell>
                                ))}
                              </Mui.TableRow>

                              {isExpanded && isBreakDateLoading ? (
                                <Mui.TableRow
                                  sx={{
                                    "& td": {
                                      backgroundColor: designColors.white,
                                    },
                                  }}
                                >
                                  <Mui.TableCell
                                    colSpan={1 + breakActiveHeaders.length}
                                    style={{ textAlign: "center" }}
                                  >
                                    <Mui.Grid container justifyContent="center">
                                      <Mui.CircularProgress
                                        size={28}
                                        sx={{ color: designColors.teal }}
                                      />
                                    </Mui.Grid>
                                  </Mui.TableCell>
                                </Mui.TableRow>
                              ) : isExpanded ? (
                                <Mui.TableRow
                                  sx={{
                                    "& td": {
                                      backgroundColor: designColors.white,
                                    },
                                  }}
                                >
                                  <Mui.TableCell
                                    colSpan={1 + breakActiveHeaders.length}
                                  >
                                    <Mui.TableContainer
                                      sx={breakShiftTableContainerSx}
                                    >
                                      {breakDateDetails.length > 0 ? (
                                        <Mui.Box
                                          sx={{
                                            p: 1.25,
                                            pb: 0.75,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                          }}
                                        >
                                          <Mui.TextField
                                            size="small"
                                            sx={{ width: 280, maxWidth: "100%" }}
                                            placeholder="Search by name, id..."
                                            value={breakDateDetailSearch}
                                            onChange={(event) => {
                                              const nextValue =
                                                event.target.value;
                                              setBreakDateDetailSearchByKey(
                                                (prev) => ({
                                                  ...prev,
                                                  [rowKey]: nextValue,
                                                }),
                                              );
                                              setBreakDateDetailPageByKey(
                                                (prev) => ({
                                                  ...prev,
                                                  [rowKey]: 0,
                                                }),
                                              );
                                            }}
                                            InputProps={{
                                              startAdornment: (
                                                <Mui.InputAdornment position="start">
                                                  <SearchRounded
                                                    sx={{
                                                      fontSize: 18,
                                                      color:
                                                        designColors.slate500,
                                                    }}
                                                  />
                                                </Mui.InputAdornment>
                                              ),
                                            }}
                                          />
                                        </Mui.Box>
                                      ) : null}
                                      {breakDateDetails.length === 0 ? (
                                        <EmptyState
                                          size={64}
                                          title="No Shifts Available"
                                          subtitle="There are no available shifts for this date."
                                          sx={{ py: 2 }}
                                        />
                                      ) : filteredBreakDateDetails.length ===
                                        0 ? (
                                        <EmptyState
                                          size={64}
                                          title="No Matching Results"
                                          subtitle="No employees match your search."
                                          sx={{ py: 2 }}
                                        />
                                      ) : (
                                        <ApproveTable sx={breakShiftTableSx}>
                                          <Mui.TableHead>
                                            <Mui.TableRow>
                                              <Mui.TableCell
                                                sx={{ width: 48 }}
                                              />
                                              {breakReportDateWiseDetailHeaders.map(
                                                (header) => (
                                                  <Mui.TableCell key={header}>
                                                    {header}
                                                  </Mui.TableCell>
                                                ),
                                              )}
                                            </Mui.TableRow>
                                          </Mui.TableHead>
                                          <Mui.TableBody>
                                            {visibleBreakDateDetails.map(
                                              (detailRow, index) => {
                                                const breakDetails =
                                                  Array.isArray(
                                                    detailRow?.break_details,
                                                  )
                                                    ? detailRow.break_details
                                                    : [];
                                                const detailRowKey = `${rowKey}-${detailRow?.employee_id || detailRow?.user_id || index}`;
                                                const isDateExpanded =
                                                  breakExpandedDateKeys.includes(
                                                    detailRowKey,
                                                  );

                                                return (
                                                  <React.Fragment
                                                    key={detailRowKey}
                                                  >
                                                    <Mui.TableRow>
                                                      <Mui.TableCell
                                                        onClick={() => {
                                                          setBreakExpandMode(
                                                            "collapse-all",
                                                          );
                                                          setBreakExpandedDateKeys(
                                                            (prev) =>
                                                              isDateExpanded
                                                                ? prev.filter(
                                                                    (key) =>
                                                                      key !==
                                                                      detailRowKey,
                                                                  )
                                                                : [
                                                                    ...prev,
                                                                    detailRowKey,
                                                                  ],
                                                          );
                                                        }}
                                                      >
                                                        <ArrowRightIcon
                                                          sx={{
                                                            color:
                                                              designColors.slate500,
                                                            cursor: "pointer",
                                                            transition:
                                                              "transform 0.2s ease",
                                                            transform:
                                                              isDateExpanded
                                                                ? "rotate(90deg)"
                                                                : "rotate(0deg)",
                                                          }}
                                                        />
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "employee_name",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "employee_id",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "department_name",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "shift",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "check_in",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "check_out",
                                                        )}
                                                      </Mui.TableCell>
                                                      <Mui.TableCell>
                                                        {getBreakDateWiseDetailValue(
                                                          detailRow,
                                                          "break",
                                                        )}
                                                      </Mui.TableCell>
                                                    </Mui.TableRow>

                                                    {isDateExpanded ? (
                                                      <Mui.TableRow>
                                                        <Mui.TableCell
                                                          colSpan={8}
                                                        >
                                                          <Mui.TableContainer
                                                            sx={
                                                              breakEntryTableContainerSx
                                                            }
                                                          >
                                                            {breakDetails.length ? (
                                                              <ApproveTable
                                                                sx={
                                                                  breakEntryTableSx
                                                                }
                                                              >
                                                                <Mui.TableHead>
                                                                  <Mui.TableRow>
                                                                    {breakReportBreakHeaders.map(
                                                                      (
                                                                        header,
                                                                      ) => (
                                                                        <Mui.TableCell
                                                                          key={
                                                                            header
                                                                          }
                                                                        >
                                                                          {
                                                                            header
                                                                          }
                                                                        </Mui.TableCell>
                                                                      ),
                                                                    )}
                                                                  </Mui.TableRow>
                                                                </Mui.TableHead>
                                                                <Mui.TableBody>
                                                                  {breakDetails.map(
                                                                    (
                                                                      shift,
                                                                      breakIndex,
                                                                    ) => (
                                                                      <Mui.TableRow
                                                                        key={
                                                                          breakIndex
                                                                        }
                                                                      >
                                                                        <Mui.TableCell>
                                                                          {getBreakStart(
                                                                            shift,
                                                                          )}
                                                                        </Mui.TableCell>
                                                                        <Mui.TableCell>
                                                                          {getBreakEnd(
                                                                            shift,
                                                                          )}
                                                                        </Mui.TableCell>
                                                                        <Mui.TableCell>
                                                                          {getBreakDuration(
                                                                            shift,
                                                                          )}
                                                                        </Mui.TableCell>
                                                                      </Mui.TableRow>
                                                                    ),
                                                                  )}
                                                                </Mui.TableBody>
                                                              </ApproveTable>
                                                            ) : (
                                                              <EmptyState
                                                                size={56}
                                                                title="No Breaks Available"
                                                                subtitle="There are no available breaks for this employee."
                                                                sx={{ py: 1.5 }}
                                                              />
                                                            )}
                                                          </Mui.TableContainer>
                                                        </Mui.TableCell>
                                                      </Mui.TableRow>
                                                    ) : null}
                                                  </React.Fragment>
                                                );
                                              },
                                            )}
                                          </Mui.TableBody>
                                        </ApproveTable>
                                      )}
                                      {compactRowPager({
                                        page: breakDateDetailsPage,
                                        pageSize: BREAK_DATE_DETAIL_PAGE_SIZE,
                                        total: filteredBreakDateDetails.length,
                                        itemLabel: "employees",
                                        onPageChange: (nextPage) =>
                                          setBreakDateDetailPageByKey(
                                            (prev) => ({
                                              ...prev,
                                              [rowKey]: nextPage,
                                            }),
                                          ),
                                      })}
                                    </Mui.TableContainer>
                                  </Mui.TableCell>
                                </Mui.TableRow>
                              ) : null}
                            </React.Fragment>
                          );
                        }

                        const isExpanded =
                          breakExpandMode === "expand-all" ||
                          breakExpandedEmployeeIds.includes(element?.user_id);
                        const breakUserDetails =
                          breakDetailsByUserId[element?.user_id] || null;
                        const isBreakUserLoading = breakLoadingUserIds.includes(
                          element?.user_id,
                        );

                        return (
                          <React.Fragment key={element?.user_id}>
                            <Mui.TableRow
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              <Mui.TableCell
                                sx={approveTableBodyCell}
                                onClick={async () => {
                                  if (isExpanded) {
                                    setBreakExpandMode("collapse-all");
                                    setBreakExpandedEmployeeIds((prev) =>
                                      prev.filter(
                                        (id) => id !== element?.user_id,
                                      ),
                                    );
                                    setBreakExpandedDateKeys((prev) =>
                                      prev.filter(
                                        (key) =>
                                          !key.startsWith(
                                            `${element?.user_id}-`,
                                          ),
                                      ),
                                    );
                                    return;
                                  }

                                  setBreakExpandMode("collapse-all");
                                  setBreakExpandedEmployeeIds((prev) =>
                                    prev.includes(element?.user_id)
                                      ? prev
                                      : [...prev, element?.user_id],
                                  );
                                  await ensureBreakReportUserDetails(
                                    element?.user_id,
                                  );
                                }}
                              >
                                <ArrowRightIcon
                                  sx={{
                                    color: designColors.slate500,
                                    cursor: "pointer",
                                    transition: "transform 0.2s ease",
                                    transform: isExpanded
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)",
                                  }}
                                />
                              </Mui.TableCell>
                              {breakActiveHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={approveTableBodyCell}
                                >
                                  {renderBreakIdentityCell(header.key, element)}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>

                            {isExpanded && isBreakUserLoading ? (
                              <Mui.TableRow
                                sx={{
                                  "& td": {
                                    backgroundColor: designColors.white,
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  colSpan={1 + breakActiveHeaders.length}
                                  style={{ textAlign: "center" }}
                                >
                                  <Mui.Grid container justifyContent="center">
                                    <Mui.CircularProgress
                                      size={28}
                                      sx={{ color: designColors.teal }}
                                    />
                                  </Mui.Grid>
                                </Mui.TableCell>
                              </Mui.TableRow>
                            ) : isExpanded ? (
                              <Mui.TableRow
                                sx={{
                                  "& td": {
                                    backgroundColor: designColors.white,
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  colSpan={1 + breakActiveHeaders.length}
                                >
                                  <Mui.TableContainer
                                    sx={breakShiftTableContainerSx}
                                  >
                                    {breakUserDetails?.checkin_details
                                      ?.length ? (
                                      <ApproveTable sx={breakShiftTableSx}>
                                        <Mui.TableHead>
                                          <Mui.TableRow>
                                            <Mui.TableCell sx={{ width: 48 }} />
                                            {breakReportDateHeaders.map(
                                              (header) => (
                                                <Mui.TableCell key={header}>
                                                  {header}
                                                </Mui.TableCell>
                                              ),
                                            )}
                                          </Mui.TableRow>
                                        </Mui.TableHead>
                                        <Mui.TableBody>
                                          {breakUserDetails.checkin_details.map(
                                            (shiftDetails, index) => {
                                              const breakDetails =
                                                Array.isArray(
                                                  shiftDetails?.break_details,
                                                )
                                                  ? shiftDetails.break_details
                                                  : [];
                                              const rowKey = `${element?.user_id}-${shiftDetails?.date}-${index}`;
                                              const isDateExpanded =
                                                breakExpandedDateKeys.includes(
                                                  rowKey,
                                                );

                                              return (
                                                <React.Fragment key={rowKey}>
                                                  <Mui.TableRow>
                                                    <Mui.TableCell
                                                      onClick={() => {
                                                        setBreakExpandMode(
                                                          "collapse-all",
                                                        );
                                                        setBreakExpandedDateKeys(
                                                          (prev) =>
                                                            isDateExpanded
                                                              ? prev.filter(
                                                                  (key) =>
                                                                    key !==
                                                                    rowKey,
                                                                )
                                                              : [
                                                                  ...prev,
                                                                  rowKey,
                                                                ],
                                                        );
                                                      }}
                                                    >
                                                      <ArrowRightIcon
                                                        sx={{
                                                          color:
                                                            designColors.slate500,
                                                          cursor: "pointer",
                                                          transition:
                                                            "transform 0.2s ease",
                                                          transform:
                                                            isDateExpanded
                                                              ? "rotate(90deg)"
                                                              : "rotate(0deg)",
                                                        }}
                                                      />
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {getCellValue(
                                                        shiftDetails?.date,
                                                      )}
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {getCellValue(
                                                        shiftDetails?.shift,
                                                      )}
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {getCellValue(
                                                        shiftDetails?.check_in_time_formatted ||
                                                          shiftDetails?.check_in_time,
                                                      )}
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {getCellValue(
                                                        shiftDetails?.check_out_time_formatted ||
                                                          shiftDetails?.check_out_time,
                                                      )}
                                                    </Mui.TableCell>
                                                    <Mui.TableCell>
                                                      {getCellValue(
                                                        shiftDetails?.break_hours ||
                                                          shiftDetails?.break_time,
                                                        "00:00",
                                                      )}
                                                    </Mui.TableCell>
                                                  </Mui.TableRow>

                                                  {isDateExpanded && (
                                                    <Mui.TableRow>
                                                      <Mui.TableCell
                                                        colSpan={6}
                                                      >
                                                        <Mui.TableContainer
                                                          sx={
                                                            breakEntryTableContainerSx
                                                          }
                                                        >
                                                          {breakDetails.length ? (
                                                            <ApproveTable
                                                              sx={
                                                                breakEntryTableSx
                                                              }
                                                            >
                                                              <Mui.TableHead>
                                                                <Mui.TableRow>
                                                                  {breakReportBreakHeaders.map(
                                                                    (
                                                                      header,
                                                                    ) => (
                                                                      <Mui.TableCell
                                                                        key={
                                                                          header
                                                                        }
                                                                      >
                                                                        {header}
                                                                      </Mui.TableCell>
                                                                    ),
                                                                  )}
                                                                </Mui.TableRow>
                                                              </Mui.TableHead>
                                                              <Mui.TableBody>
                                                                {breakDetails.map(
                                                                  (
                                                                    shift,
                                                                    breakIndex,
                                                                  ) => (
                                                                    <Mui.TableRow
                                                                      key={
                                                                        breakIndex
                                                                      }
                                                                    >
                                                                      <Mui.TableCell>
                                                                        {getBreakStart(
                                                                          shift,
                                                                        )}
                                                                      </Mui.TableCell>
                                                                      <Mui.TableCell>
                                                                        {getBreakEnd(
                                                                          shift,
                                                                        )}
                                                                      </Mui.TableCell>
                                                                      <Mui.TableCell>
                                                                        {getBreakDuration(
                                                                          shift,
                                                                        )}
                                                                      </Mui.TableCell>
                                                                    </Mui.TableRow>
                                                                  ),
                                                                )}
                                                              </Mui.TableBody>
                                                            </ApproveTable>
                                                          ) : (
                                                            <EmptyState
                                                              size={56}
                                                              title="No Breaks Available"
                                                              subtitle="There are no available breaks for this employee."
                                                              sx={{ py: 1.5 }}
                                                            />
                                                          )}
                                                        </Mui.TableContainer>
                                                      </Mui.TableCell>
                                                    </Mui.TableRow>
                                                  )}
                                                </React.Fragment>
                                              );
                                            },
                                          )}
                                        </Mui.TableBody>
                                      </ApproveTable>
                                    ) : (
                                      <EmptyState
                                        size={64}
                                        title="No Shifts Available"
                                        subtitle="There are no available shifts for this employee."
                                        sx={{ py: 2 }}
                                      />
                                    )}
                                  </Mui.TableContainer>
                                </Mui.TableCell>
                              </Mui.TableRow>
                            ) : null}
                          </React.Fragment>
                        );
                      })
                    )}
                  </Mui.TableBody>
                </ApproveTable>
              </Mui.TableContainer>
            </Mui.Box>

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: workingHourData?.length || 0,
                itemLabel: "records",
                onPageChange: setLeavePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions: isBreakDateWiseView
                  ? DATE_WISE_ROWS_PER_PAGE_OPTIONS
                  : rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );

  return (
    <React.Fragment>
      {/* ── Header + FilterSection ─────────────────────────────────────────── */}
      {reportedType !== WORKING_HOURS_REPORT_TYPE && (
        <ThemeProvider theme={withDesignFont(theme)}>
          {/* This screen only ever renders embedded inside TeamZone
              Reports' grouped category page (Reports/index.js), which
              already supplies designShell.pageSurface (background +
              min-height) and the page's horizontal padding. Re-applying
              pageSurface/px here double-stacked the min-height and
              padding, forcing extra blank space below short reports and
              a narrower table than every other grouped report. */}
          <Mui.Box
            sx={{
              pt: 0,
            }}
          >
            {/* Filters now sit in their own row directly under the page
                title — same placement as Employee Attendance Report
                (EsslReport) — instead of floating beside the title via
                setReportHeaderActions. */}
            <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
              {headerFilterControls}
            </Mui.Box>
            {(reportedType === SUMMARIZED_WORKING_HOURS_TYPE ||
              reportedType === DETAILED_WORKING_HOURS_TYPE ||
              reportedType === TIME_CARD_REPORT_TYPE) && (
              <Mui.Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(2, minmax(0, 1fr))",
                    lg: "repeat(3, minmax(0, 1fr))",
                    xl: "repeat(5, minmax(0, 1fr))",
                  },
                  "@media (max-width:599.95px)": {
                    "& > :last-child:nth-child(odd)": { gridColumn: "1 / -1" },
                  },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                {(reportedType === TIME_CARD_REPORT_TYPE
                  ? timeCardSummaryCards
                  : workingHoursSummaryCards
                ).map((card) => (
                  <StatCard
                    key={card.key}
                    icon={card.icon}
                    title={card.title}
                    value={card.value}
                    accent={card.accent}
                    tint={card.tint}
                  />
                ))}
              </Mui.Box>
            )}
          </Mui.Box>
        </ThemeProvider>
      )}
      {/* ───────────────────────────────────────────────────────────────────── */}

      {reportedType === BREAK_REPORT_TYPE && !loading ? (
        renderBreakReport()
      ) : (reportedType === SUMMARIZED_WORKING_HOURS_TYPE ||
          reportedType === DETAILED_WORKING_HOURS_TYPE) &&
        !loading ? (
        <ThemeProvider theme={withDesignFont(theme)}>
          <FilterProvider
            domain={domain}
            userDetails={currentUserDetails}
            subModuleId={subModuleId}
          >
            {isDetailedDateWiseView ? (
              renderDetailedDateWiseView()
            ) : (
              /* Same reasoning as the header block above — the grouped
                Reports page already supplies pageSurface, so this table
                shell only needs its own sectionCard styling. */
              <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
                {/* CustomFilter's sticky toolbar must sit OUTSIDE the
                  sectionCard, as a sibling — matching the canonical
                  listview-table-container pattern (Administration/ListView.js,
                  Approve/OverTime/index.js). Nesting it inside a sectionCard
                  that also sets overflow:"hidden" put a position:sticky
                  element inside an overflow:hidden ancestor, which breaks its
                  sticky containing block and left a stray gap above the
                  toolbar instead of the two sitting flush together.
                  This wrapper itself must stay overflow:"visible" (the
                  default) for the same reason — it's a shared ancestor of
                  BOTH the toolbar above and the footer bar further below,
                  so overflowX:"hidden" here (as it briefly was) hijacked
                  both of their sticky containing blocks away from
                  .main-content at once, which is what made neither the
                  header nor the footer actually stick. */}
                <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
                  <CustomFilter
                    title={
                      reportedType === SUMMARIZED_WORKING_HOURS_TYPE
                        ? "Working Hours Summary"
                        : reportedType === PUNCH_DETAILS_TYPE
                          ? "Punch Details"
                          : DETAILED_WORKING_HOURS_TYPE
                    }
                    titleOverride={titleOverride}
                    filterHeaders={visibleHeaders}
                    data={workingHoursDetails}
                    filteredData={workingHourData}
                    onFilteredDataChange={handleFilteredDataChange}
                    screenAccess={check_access}
                    isAttendanceScreenReport={true}
                    showDownloadOptions={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                    }
                    showDownloadTypeSelector={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                    }
                    downloadOptionStates={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                        ? {
                            summarized: {
                              disabled:
                                !Array.isArray(workingHourData) ||
                                workingHourData.length === 0,
                              reason:
                                "No summarized data is available to export.",
                            },
                            detailed: {
                              disabled:
                                !Array.isArray(workingHourData) ||
                                workingHourData.length === 0,
                              reason:
                                "No detailed data is available to export.",
                            },
                          }
                        : null
                    }
                    downloadFormats={["excel", "pdf", "csv"]}
                    includeMetadata={true}
                    metadata={getReportMetadata()}
                    getDetailedHeaders={() => visibleHeaders}
                    customDownloadHandler={
                      reportedType === SUMMARIZED_WORKING_HOURS_TYPE
                        ? handleSummarizedWorkingHoursDownload
                        : undefined
                    }
                    customSummarizedDownloadHandler={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                        ? handleDetailedEmployeeWiseSummarizedDownload
                        : undefined
                    }
                    customDetailedDownloadHandler={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                        ? handleDetailedEmployeeWiseDetailedDownload
                        : undefined
                    }
                    pdfRowCountOverride={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                        ? detailedPdfRowCount
                        : null
                    }
                    showtitle_count={true}
                    hideCount={true}
                    newButtonComponent={
                      reportedType === DETAILED_WORKING_HOURS_TYPE
                        ? detailedExpandControl
                        : newButtonComponent
                    }
                    labeledToolbar={true}
                    searchPlaceholder="Search by employee, department..."
                  />
                </Box>
                <Box
                  sx={{
                    ...designShell.sectionCard,
                    mt: 1.5,
                    borderRadius: 0,
                  }}
                >
                  <Mui.Box>
                    <Mui.TableContainer
                      sx={{
                        boxSizing: "border-box",
                        width: "100%",
                        maxWidth: "100%",
                        // Matches Admin's Users/Companies list and this
                        // module's own Approve tables (Leave, OverTime,
                        // ...) exactly: overflowX must be explicitly
                        // "visible" — MUI's TableContainer defaults to
                        // overflowX:"auto" internally even when your own
                        // sx never mentions it, and "auto" on either axis
                        // (with no maxHeight) turns this box into a real
                        // scroll container that hijacks the sticky
                        // header's top offset away from .main-content
                        // (the page's actual, single scroll region) and
                        // adds a second local scrollbar alongside it.
                        // tableLayout:"fixed" + width:"100%" on the table
                        // below makes it mathematically incapable of ever
                        // needing to overflow this box, so there's never
                        // horizontal content to scroll to in the first
                        // place — no scroll container needed here at all.
                        overflowX: { xs: "auto", sm: "visible" },
                        overflowY: { xs: "auto", sm: "visible" },
                        pb: footerHeight ? `${footerHeight}px` : 0,
                        borderLeft: `1px solid ${designColors.slate200}`,
                        borderRight: `1px solid ${designColors.slate200}`,
                        borderBottom: `1px solid ${designColors.slate200}`,
                      }}
                    >
                      <ApproveTable
                        stickyHeader
                        sx={{
                          width: "100%",
                          tableLayout: "fixed",
                        }}
                      >
                        <Mui.TableHead sx={designShell.tableStickyHead}>
                          <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                            {reportedType === DETAILED_WORKING_HOURS_TYPE ? (
                              <Mui.TableCell
                                sx={{
                                  ...approveTableHeadCell,
                                  top: APP_BAR_OFFSET + customFilterHeight,
                                  backgroundColor: designColors.slate100,
                                  borderTopLeftRadius: 0,
                                  // % of the total, not a literal 48px — see
                                  // the comment on visibleHeaderColumnWidthTotal
                                  // above for why this has to be a share of
                                  // 100%, not a fixed pixel count.
                                  width: getWorkingHoursColumnWidthPercent(48),
                                }}
                              ></Mui.TableCell>
                            ) : null}
                            {visibleHeaders.map((header, headerIndex) => (
                              <Mui.TableCell
                                key={header.key}
                                sx={{
                                  ...approveTableHeadCell,
                                  top: APP_BAR_OFFSET + customFilterHeight,
                                  backgroundColor: designColors.slate100,
                                  // Proportional share of the table's fixed
                                  // 100% width (see visibleHeaderColumnWidthTotal
                                  // above) — every column's % share always
                                  // sums to exactly 100%, so the table can
                                  // never exceed its container regardless of
                                  // how many columns are visible.
                                  width: getWorkingHoursColumnWidthPercent(
                                    getWorkingHoursColumnWidth(header),
                                  ),
                                  ...(reportedType !==
                                    DETAILED_WORKING_HOURS_TYPE &&
                                  headerIndex === 0
                                    ? { borderTopLeftRadius: 0 }
                                    : {}),
                                  ...(headerIndex === visibleHeaders.length - 1
                                    ? { borderTopRightRadius: 0 }
                                    : {}),
                                }}
                              >
                                <TruncatedTableValue value={header.name} />
                              </Mui.TableCell>
                            ))}
                          </Mui.TableRow>
                        </Mui.TableHead>
                        <Mui.TableBody>
                          {workingHourData.length === 0 && loading === false ? (
                            <TableEmptyState dataLength={workingHoursDetails} />
                          ) : (
                            visibleDetailedRows.map((element) => (
                              <React.Fragment
                                key={element?.user_id || element?.employee_id}
                              >
                                <Mui.TableRow
                                  hover
                                  sx={{
                                    ...designShell.tableRowHover,
                                    "&:last-child td": {
                                      borderBottom: "none",
                                    },
                                    "& td": {
                                      backgroundColor: designColors.white,
                                    },
                                  }}
                                >
                                  {reportedType ===
                                  DETAILED_WORKING_HOURS_TYPE ? (
                                    <Mui.TableCell
                                      align="left"
                                      sx={{
                                        ...approveTableBodyCell,
                                        width: 40,
                                        px: 0.5,
                                        fontSize: 0,
                                        overflow: "visible",
                                        textOverflow: "clip",
                                        whiteSpace: "normal",
                                      }}
                                      onClick={() => {
                                        const isExpanded =
                                          detailedExpandMode === "expand-all" ||
                                          detailedExpandedEmployeeIds.includes(
                                            element?.user_id,
                                          );

                                        if (isExpanded) {
                                          setDetailedExpandMode("collapse-all");
                                          setDetailedExpandedEmployeeIds(
                                            (prev) =>
                                              prev.filter(
                                                (id) => id !== element?.user_id,
                                              ),
                                          );
                                          setBreakExpandableDate(0);
                                          if (
                                            element?.user_id ===
                                            personExpandableId
                                          ) {
                                            setPersonExpandableId(0);
                                          }
                                        } else {
                                          setDetailedExpandMode("collapse-all");
                                          setDetailedExpandedEmployeeIds(
                                            (prev) =>
                                              prev.includes(element?.user_id)
                                                ? prev
                                                : [...prev, element?.user_id],
                                          );
                                          setBreakExpandableDate(0);
                                          setPersonExpandableId(
                                            element?.user_id,
                                          );
                                          ensureDetailedUserDetails(
                                            element?.user_id,
                                          );
                                        }
                                      }}
                                    >
                                      <Mui.IconButton
                                        size="small"
                                        sx={{
                                          p: 0.25,
                                          color: designColors.slate500,
                                        }}
                                      >
                                        <ArrowRightIcon
                                          sx={{
                                            transition: "transform 0.2s ease",
                                            transform:
                                              detailedExpandMode ===
                                                "expand-all" ||
                                              detailedExpandedEmployeeIds.includes(
                                                element?.user_id,
                                              )
                                                ? "rotate(90deg)"
                                                : "rotate(0deg)",
                                          }}
                                        />
                                      </Mui.IconButton>
                                    </Mui.TableCell>
                                  ) : null}
                                  {visibleHeaders.map((header) => (
                                    <React.Fragment key={header.key}>
                                      {header?.key === "employee_name" ? (
                                        <Mui.TableCell
                                          sx={approveTableBodyCell}
                                        >
                                          <TruncatedTableValue
                                            value={
                                              element?.employee_name || "N/A"
                                            }
                                          >
                                            <Mui.Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              sx={{
                                                minWidth: 0,
                                                width: "100%",
                                              }}
                                            >
                                              <Mui.Avatar
                                                sx={{ width: 28, height: 28 }}
                                                alt={element?.employee_name
                                                  ?.charAt(0)
                                                  .toUpperCase()}
                                                src={element?.image_url}
                                              />
                                              <Mui.Typography
                                                noWrap
                                                data-table-value-content
                                                sx={{
                                                  minWidth: 0,
                                                  fontWeight:
                                                    detailedExpandMode ===
                                                      "expand-all" ||
                                                    detailedExpandedEmployeeIds.includes(
                                                      element?.user_id,
                                                    )
                                                      ? 700
                                                      : 500,
                                                  // Explicit slate900 instead
                                                  // of "inherit" — OverTime
                                                  // uses the same
                                                  // expanded/not-expanded ?
                                                  // slate900 : "inherit"
                                                  // logic and its Name text
                                                  // matches its own Employee
                                                  // ID column tone-for-tone,
                                                  // but here "inherit" wasn't
                                                  // reliably picking up
                                                  // approveTableBodyCell's
                                                  // color through the
                                                  // Stack/Typography nesting,
                                                  // leaving Name visibly
                                                  // darker than its sibling
                                                  // cells in the same row.
                                                  // Both states now resolve
                                                  // to the same explicit
                                                  // value the plain-text
                                                  // cells already get.
                                                  color: designColors.slate900,
                                                  fontSize: 14,
                                                }}
                                              >
                                                {element?.employee_name ||
                                                  "N/A"}
                                              </Mui.Typography>
                                            </Mui.Stack>
                                          </TruncatedTableValue>
                                        </Mui.TableCell>
                                      ) : (
                                        <Mui.TableCell
                                          align="left"
                                          sx={approveTableBodyCell}
                                        >
                                          <TruncatedTableValue
                                            value={
                                              element[header.key] ||
                                              element[header.key] === 0
                                                ? element[header.key]
                                                : "N/A"
                                            }
                                          />
                                        </Mui.TableCell>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </Mui.TableRow>

                                {(detailedExpandMode === "expand-all" ||
                                  detailedExpandedEmployeeIds.includes(
                                    element?.user_id,
                                  )) &&
                                detailedLoadingUserIds.includes(
                                  element?.user_id,
                                ) ? (
                                  <Mui.TableRow sx={tableRowSx}>
                                    <Mui.TableCell
                                      colSpan={
                                        visibleHeaders.length +
                                        (reportedType ===
                                        DETAILED_WORKING_HOURS_TYPE
                                          ? 1
                                          : 0)
                                      }
                                      style={{ textAlign: "center" }}
                                    >
                                      <Mui.Box
                                        role="status"
                                        aria-label="Loading working hours"
                                      >
                                        <Loader minHeight={120} />
                                      </Mui.Box>
                                    </Mui.TableCell>
                                  </Mui.TableRow>
                                ) : detailedExpandMode === "expand-all" ||
                                  detailedExpandedEmployeeIds.includes(
                                    element?.user_id,
                                  ) ? (
                                  <Mui.TableRow
                                    sx={{
                                      "& td": {
                                        backgroundColor: designColors.white,
                                      },
                                    }}
                                  >
                                    <Mui.TableCell
                                      colSpan={
                                        visibleHeaders.length +
                                        (reportedType ===
                                        DETAILED_WORKING_HOURS_TYPE
                                          ? 1
                                          : 0)
                                      }
                                    >
                                      <Mui.TableContainer
                                        sx={workingHoursDetailTableContainerSx}
                                      >
                                        {detailedErrorUserIds.includes(
                                          element?.user_id,
                                        ) ? (
                                          <Mui.Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="center"
                                            sx={{ py: 2 }}
                                          >
                                            <Mui.Typography variant="body2">
                                              Couldn't load working hours.
                                            </Mui.Typography>
                                            <Mui.Button
                                              size="small"
                                              onClick={() =>
                                                ensureDetailedUserDetails(
                                                  element?.user_id,
                                                )
                                              }
                                            >
                                              Retry
                                            </Mui.Button>
                                          </Mui.Stack>
                                        ) : element?.check_ins?.length !== 0 ? (
                                          detailedDetailsByUserId[
                                            element?.user_id
                                          ] && (
                                            <ApproveTable
                                              sx={workingHoursDetailTableSx}
                                            >
                                              <Mui.TableHead>
                                                <Mui.TableRow sx={tableRowSx}>
                                                  <Mui.TableCell></Mui.TableCell>
                                                  {expandVisibleHeaders.map(
                                                    (header) => (
                                                      <Mui.TableCell
                                                        key={header.key}
                                                      >
                                                        {header.name}
                                                      </Mui.TableCell>
                                                    ),
                                                  )}
                                                  <Mui.TableCell>
                                                    Action
                                                  </Mui.TableCell>
                                                </Mui.TableRow>
                                              </Mui.TableHead>
                                              <Mui.TableBody>
                                                {(
                                                  detailedDetailsByUserId[
                                                    element?.user_id
                                                  ]?.checkin_details || []
                                                ).map((shiftDetails, index) =>
                                                  shiftDetails?.status ===
                                                    "HL" ||
                                                  shiftDetails?.status ===
                                                    "L" ? (
                                                    <>
                                                      <Mui.TableRow
                                                        key={index}
                                                        sx={tableRowSx}
                                                      >
                                                        <Mui.TableCell>
                                                          {" "}
                                                        </Mui.TableCell>
                                                        {expandVisibleHeaders.map(
                                                          (header) => {
                                                            switch (
                                                              header.key
                                                            ) {
                                                              case "date":
                                                                return (
                                                                  <Mui.TableCell key="date">
                                                                    {
                                                                      shiftDetails?.date
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "shift":
                                                              case "check_in":
                                                              case "check_out":
                                                              case "shift_hours":
                                                                return (
                                                                  <Mui.TableCell
                                                                    key={
                                                                      header.key
                                                                    }
                                                                  >
                                                                    {" "}
                                                                  </Mui.TableCell>
                                                                );
                                                              case "break":
                                                                return shiftDetails?.status ===
                                                                  "L" ? (
                                                                  <Mui.TableCell key="break"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="break">
                                                                    {
                                                                      shiftDetails?.break_hours
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "over_time":
                                                                return shiftDetails?.status ===
                                                                  "L" ? (
                                                                  <Mui.TableCell key="over_time"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="over_time">
                                                                    {
                                                                      shiftDetails?.over_time
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "working_hours":
                                                                return shiftDetails?.status ===
                                                                  "L" ? (
                                                                  <Mui.TableCell key="working_hours"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="working_hours">
                                                                    {
                                                                      shiftDetails?.working_hours
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "leave_credit_hours":
                                                                return (
                                                                  <Mui.TableCell key="leave_credit_hours">
                                                                    {shiftDetails.status ===
                                                                      "L" ||
                                                                    shiftDetails.status ===
                                                                      "H" ? (
                                                                      <Mui.Typography>
                                                                        {
                                                                          shiftDetails.Leave_credit_hours
                                                                        }
                                                                      </Mui.Typography>
                                                                    ) : shiftDetails.status ===
                                                                      "HL" ? (
                                                                      <Mui.Typography>
                                                                        {
                                                                          shiftDetails.Leave_credit_hours
                                                                        }
                                                                      </Mui.Typography>
                                                                    ) : null}
                                                                  </Mui.TableCell>
                                                                );
                                                              case "status":
                                                                return (
                                                                  <Mui.TableCell key="status">
                                                                    <Mui.Tooltip
                                                                      arrow
                                                                      slotProps={
                                                                        tooltipStyleProps
                                                                      }
                                                                      placement="top"
                                                                      title={getStatusTooltip(
                                                                        shiftDetails.status,
                                                                      )}
                                                                    >
                                                                      <Mui.Typography
                                                                        sx={{
                                                                          display:
                                                                            "inline-block",
                                                                          width:
                                                                            "28px",
                                                                          color:
                                                                            shiftDetails.status ===
                                                                            "W"
                                                                              ? themes?.headLine
                                                                              : themes.whiteColor,
                                                                          height:
                                                                            "28px",
                                                                          textAlign:
                                                                            "center",
                                                                          lineHeight:
                                                                            "29px",
                                                                          border:
                                                                            "none",
                                                                          backgroundColor:
                                                                            shiftDetails?.color,
                                                                          cursor:
                                                                            "pointer",
                                                                        }}
                                                                      >
                                                                        {shiftDetails.status ===
                                                                        "HL" ? (
                                                                          <Mui.Grid
                                                                            container
                                                                          >
                                                                            <Mui.Typography
                                                                              sx={{
                                                                                ...attendanceStatusHalfBox,
                                                                                backgroundColor:
                                                                                  shiftDetails?.color,
                                                                              }}
                                                                            >
                                                                              H
                                                                            </Mui.Typography>
                                                                            <Mui.Typography
                                                                              sx={{
                                                                                ...attendanceStatusHalfBox,
                                                                                backgroundColor:
                                                                                  shiftDetails?.color,
                                                                              }}
                                                                            >
                                                                              L
                                                                            </Mui.Typography>
                                                                          </Mui.Grid>
                                                                        ) : (
                                                                          shiftDetails.status
                                                                        )}
                                                                      </Mui.Typography>
                                                                    </Mui.Tooltip>
                                                                  </Mui.TableCell>
                                                                );
                                                              default:
                                                                return null;
                                                            }
                                                          },
                                                        )}
                                                        <Mui.TableCell key="action">
                                                          <Mui.IconButton
                                                            onClick={() => {
                                                              if (
                                                                checkFirstLevelApproverPermission(
                                                                  userScreenAccess?.module_details,
                                                                  "Reports",
                                                                  "Reports",
                                                                )
                                                              ) {
                                                                if (
                                                                  shiftDetails.shift !==
                                                                    "Unassigned" &&
                                                                  shiftDetails?.user_id !==
                                                                    currentUserDetails?.id
                                                                ) {
                                                                  setDepartmentIdValue(
                                                                    shiftDetails?.department_id,
                                                                  );
                                                                  setShiftDetails(
                                                                    {
                                                                      user_id:
                                                                        shiftDetails?.user_id,
                                                                      shift_filter_date:
                                                                        shiftDetails?.date
                                                                          .split(
                                                                            "-",
                                                                          )
                                                                          .reverse()
                                                                          .join(
                                                                            "-",
                                                                          ),
                                                                      date: shiftDetails?.date,
                                                                      user_name:
                                                                        shiftDetails?.user_name,
                                                                      shift_name:
                                                                        shiftDetails?.shift,
                                                                      latest_pay_date:
                                                                        shiftDetails?.latest_pay_date,
                                                                      type: shiftDetails?.status,
                                                                    },
                                                                  );
                                                                  setOpenSidePeek(
                                                                    true,
                                                                  );
                                                                } else {
                                                                  if (
                                                                    shiftDetails?.user_id ===
                                                                    currentUserDetails?.id
                                                                  ) {
                                                                    showToast(
                                                                      "error",
                                                                      "Your data cannot be edited by yourself",
                                                                    );
                                                                  } else {
                                                                    showToast(
                                                                      "error",
                                                                      "Unassigned shifts cannot be edited",
                                                                    );
                                                                  }
                                                                }
                                                              } else {
                                                                showToast(
                                                                  "error",
                                                                  "Access denied: You are not authorized to modify the attendance record",
                                                                );
                                                              }
                                                            }}
                                                          >
                                                            <DriveFileRenameOutline />
                                                          </Mui.IconButton>
                                                        </Mui.TableCell>
                                                      </Mui.TableRow>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <Mui.TableRow
                                                        key={index}
                                                        sx={tableRowSx}
                                                      >
                                                        <Mui.TableCell
                                                          sx={{
                                                            cursor: "pointer",
                                                          }}
                                                          onClick={() =>
                                                            setBreakExpandableDate(
                                                              shiftDetails?.date ===
                                                                breakExpandableDate
                                                                ? 0
                                                                : shiftDetails?.date,
                                                            )
                                                          }
                                                        >
                                                          <ArrowRightIcon
                                                            sx={{
                                                              color:
                                                                designColors.slate500,
                                                              cursor: "pointer",
                                                              transition:
                                                                "transform 0.2s ease",
                                                              transform:
                                                                shiftDetails?.date ===
                                                                breakExpandableDate
                                                                  ? "rotate(90deg)"
                                                                  : "rotate(0deg)",
                                                            }}
                                                          />
                                                        </Mui.TableCell>
                                                        {expandVisibleHeaders.map(
                                                          (header) => {
                                                            switch (
                                                              header.key
                                                            ) {
                                                              case "date":
                                                                return (
                                                                  <Mui.TableCell key="date">
                                                                    {
                                                                      shiftDetails?.date
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "shift":
                                                                return (
                                                                  <Mui.TableCell key="shift">
                                                                    {
                                                                      shiftDetails?.shift
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "check_in":
                                                                return (
                                                                  <Mui.TableCell key="check_in">
                                                                    {
                                                                      shiftDetails?.check_in_time_formatted
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "check_out":
                                                                return (
                                                                  <Mui.TableCell key="check_out">
                                                                    {
                                                                      shiftDetails?.check_out_time_formatted
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "break":
                                                                return shiftDetails.status ===
                                                                  "A" ||
                                                                  shiftDetails.status ===
                                                                    "W" ? (
                                                                  <Mui.TableCell key="break"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="break">
                                                                    {
                                                                      shiftDetails?.break_hours
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "shift_hours":
                                                                return shiftDetails.status ===
                                                                  "A" ||
                                                                  shiftDetails.status ===
                                                                    "W" ? (
                                                                  <Mui.TableCell key="shift_hours"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="shift_hours">
                                                                    {shiftDetails?.shift_hours
                                                                      ? shiftDetails?.shift_hours
                                                                      : "N/A"}
                                                                  </Mui.TableCell>
                                                                );
                                                              case "over_time":
                                                                return shiftDetails.status ===
                                                                  "A" ||
                                                                  shiftDetails.status ===
                                                                    "W" ? (
                                                                  <Mui.TableCell key="over_time"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="over_time">
                                                                    {
                                                                      shiftDetails?.over_time
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "working_hours":
                                                                return shiftDetails.status ===
                                                                  "A" ||
                                                                  shiftDetails.status ===
                                                                    "W" ? (
                                                                  <Mui.TableCell key="working_hours"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="working_hours">
                                                                    {
                                                                      shiftDetails?.working_hours
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "leave_credit_hours":
                                                                return shiftDetails.status ===
                                                                  "A" ||
                                                                  shiftDetails.status ===
                                                                    "W" ||
                                                                  shiftDetails.status ===
                                                                    "P" ||
                                                                  shiftDetails.status ===
                                                                    "PR" ? (
                                                                  <Mui.TableCell key="leave_credit_hours"></Mui.TableCell>
                                                                ) : (
                                                                  <Mui.TableCell key="leave_credit_hours">
                                                                    {
                                                                      shiftDetails?.Leave_credit_hours
                                                                    }
                                                                  </Mui.TableCell>
                                                                );
                                                              case "status":
                                                                return (
                                                                  <Mui.TableCell key="status">
                                                                    <Mui.Tooltip
                                                                      arrow
                                                                      slotProps={
                                                                        tooltipStyleProps
                                                                      }
                                                                      placement="top"
                                                                      title={getStatusTooltip(
                                                                        shiftDetails.status,
                                                                      )}
                                                                    >
                                                                      <Mui.Typography
                                                                        sx={{
                                                                          display:
                                                                            "inline-block",
                                                                          width:
                                                                            "28px",
                                                                          color:
                                                                            shiftDetails.status ===
                                                                            "W"
                                                                              ? themes.headLine
                                                                              : themes.whiteColor,
                                                                          height:
                                                                            "28px",
                                                                          border:
                                                                            "none",
                                                                          textAlign:
                                                                            "center",
                                                                          lineHeight:
                                                                            "29px",
                                                                          backgroundColor:
                                                                            shiftDetails?.color,
                                                                          cursor:
                                                                            "pointer",
                                                                        }}
                                                                      >
                                                                        {shiftDetails.status ===
                                                                          "PL" ||
                                                                        shiftDetails.status ===
                                                                          "LP" ||
                                                                        shiftDetails.status ===
                                                                          "AL" ||
                                                                        shiftDetails.status ===
                                                                          "LA" ? (
                                                                          <Mui.Grid
                                                                            container
                                                                          >
                                                                            {shiftDetails.status ===
                                                                              "PL" && (
                                                                              <>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  P
                                                                                </Mui.Typography>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  L
                                                                                </Mui.Typography>
                                                                              </>
                                                                            )}
                                                                            {shiftDetails.status ===
                                                                              "LP" && (
                                                                              <>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  L
                                                                                </Mui.Typography>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  P
                                                                                </Mui.Typography>
                                                                              </>
                                                                            )}
                                                                            {shiftDetails.status ===
                                                                              "AL" && (
                                                                              <>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  A
                                                                                </Mui.Typography>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  L
                                                                                </Mui.Typography>
                                                                              </>
                                                                            )}
                                                                            {shiftDetails.status ===
                                                                              "LA" && (
                                                                              <>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  L
                                                                                </Mui.Typography>
                                                                                <Mui.Typography
                                                                                  sx={{
                                                                                    ...attendanceStatusHalfBox,
                                                                                    backgroundColor:
                                                                                      shiftDetails?.color,
                                                                                  }}
                                                                                >
                                                                                  A
                                                                                </Mui.Typography>
                                                                              </>
                                                                            )}
                                                                          </Mui.Grid>
                                                                        ) : (
                                                                          shiftDetails.status
                                                                        )}
                                                                      </Mui.Typography>
                                                                    </Mui.Tooltip>
                                                                  </Mui.TableCell>
                                                                );
                                                              default:
                                                                return null;
                                                            }
                                                          },
                                                        )}

                                                        <Mui.TableCell key="action">
                                                          <Mui.Grid
                                                            container
                                                            xs={12}
                                                          >
                                                            <Mui.Grid
                                                              item
                                                              xs={5}
                                                            >
                                                              <Mui.IconButton
                                                                onClick={() => {
                                                                  if (
                                                                    checkFirstLevelApproverPermission(
                                                                      userScreenAccess?.module_details,
                                                                      "Reports",
                                                                      "Detailed Working Hours",
                                                                    )
                                                                  ) {
                                                                    if (
                                                                      (shiftDetails.shift !==
                                                                        "Unassigned" &&
                                                                        shiftDetails?.user_id !==
                                                                          currentUserDetails?.id) ||
                                                                      ([
                                                                        adminRoleName,
                                                                        supportLabel,
                                                                      ].includes(
                                                                        currentUserDetails?.type,
                                                                      ) &&
                                                                        shiftDetails.shift !==
                                                                          "Unassigned")
                                                                    ) {
                                                                      setDepartmentIdValue(
                                                                        shiftDetails?.department_id,
                                                                      );
                                                                      setShiftDetails(
                                                                        {
                                                                          user_id:
                                                                            shiftDetails?.user_id,
                                                                          shift_filter_date:
                                                                            shiftDetails?.date
                                                                              .split(
                                                                                "-",
                                                                              )
                                                                              .reverse()
                                                                              .join(
                                                                                "-",
                                                                              ),
                                                                          date: shiftDetails?.date,
                                                                          user_name:
                                                                            shiftDetails?.user_name,
                                                                          shift_name:
                                                                            shiftDetails?.shift,
                                                                          latest_pay_date:
                                                                            shiftDetails?.latest_pay_date,
                                                                          type: shiftDetails?.status,
                                                                          image_url:
                                                                            shiftDetails?.image_url,
                                                                          employee_id:
                                                                            shiftDetails?.employee_id,
                                                                          department_name:
                                                                            shiftDetails?.department_name,
                                                                          user_type:
                                                                            shiftDetails?.user_type,
                                                                        },
                                                                      );
                                                                      setOpenSidePeek(
                                                                        true,
                                                                      );
                                                                    } else {
                                                                      if (
                                                                        shiftDetails?.user_id ===
                                                                        currentUserDetails?.id
                                                                      ) {
                                                                        showToast(
                                                                          "error",
                                                                          "Your data cannot be edited by yourself",
                                                                        );
                                                                      } else {
                                                                        showToast(
                                                                          "error",
                                                                          "Unassigned shifts cannot be edited",
                                                                        );
                                                                      }
                                                                    }
                                                                  } else {
                                                                    showToast(
                                                                      "error",
                                                                      "Access denied: You are not authorized to modify the attendance record",
                                                                    );
                                                                  }
                                                                }}
                                                              >
                                                                <DriveFileRenameOutline />
                                                              </Mui.IconButton>
                                                            </Mui.Grid>
                                                            <Mui.Grid
                                                              item
                                                              xs={5}
                                                            ></Mui.Grid>
                                                          </Mui.Grid>
                                                        </Mui.TableCell>
                                                      </Mui.TableRow>

                                                      {breakExpandableDate ===
                                                        shiftDetails?.date && (
                                                        <Mui.TableRow
                                                          sx={tableRowSx}
                                                        >
                                                          <Mui.TableCell
                                                            colSpan={
                                                              expandVisibleHeaders.length +
                                                              2
                                                            }
                                                          >
                                                            <Mui.TableContainer
                                                              sx={{
                                                                marginTop: 0,
                                                                marginLeft:
                                                                  "auto",
                                                                marginRight:
                                                                  "auto",
                                                                width: "100%",
                                                              }}
                                                              className="w-96 border rounded"
                                                            >
                                                              {shiftDetails
                                                                ?.break_details
                                                                ?.length !==
                                                              0 ? (
                                                                <ApproveTable className="border">
                                                                  <Mui.TableHead
                                                                    sx={{
                                                                      backgroundColor:
                                                                        themes.primary,
                                                                    }}
                                                                  >
                                                                    <Mui.TableRow
                                                                      sx={
                                                                        tableRowSx
                                                                      }
                                                                    >
                                                                      <Mui.TableCell
                                                                        sx={{
                                                                          color:
                                                                            themes.headLine,
                                                                          fontWeight:
                                                                            "bold",
                                                                        }}
                                                                      >
                                                                        Break
                                                                        Start
                                                                      </Mui.TableCell>
                                                                      <Mui.TableCell
                                                                        sx={{
                                                                          color:
                                                                            themes.headLine,
                                                                          fontWeight:
                                                                            "bold",
                                                                        }}
                                                                      >
                                                                        Break
                                                                        End
                                                                      </Mui.TableCell>
                                                                      <Mui.TableCell
                                                                        sx={{
                                                                          color:
                                                                            themes.headLine,
                                                                          fontWeight:
                                                                            "bold",
                                                                        }}
                                                                      >
                                                                        Break-Time
                                                                      </Mui.TableCell>
                                                                    </Mui.TableRow>
                                                                  </Mui.TableHead>
                                                                  <Mui.TableBody>
                                                                    {shiftDetails?.break_details?.map(
                                                                      (
                                                                        shift,
                                                                        index,
                                                                      ) => (
                                                                        <Mui.TableRow
                                                                          key={
                                                                            index
                                                                          }
                                                                          sx={
                                                                            tableRowSx
                                                                          }
                                                                        >
                                                                          <Mui.TableCell>
                                                                            {
                                                                              shift?.break_out
                                                                            }
                                                                          </Mui.TableCell>
                                                                          <Mui.TableCell>
                                                                            {
                                                                              shift?.break_in
                                                                            }
                                                                          </Mui.TableCell>
                                                                          <Mui.TableCell>
                                                                            {
                                                                              shift?.individual_break_time
                                                                            }
                                                                          </Mui.TableCell>
                                                                        </Mui.TableRow>
                                                                      ),
                                                                    )}
                                                                  </Mui.TableBody>
                                                                </ApproveTable>
                                                              ) : (
                                                                <EmptyState
                                                                  size={56}
                                                                  title="No Breaks Available"
                                                                  subtitle="There are no available breaks for this employee."
                                                                  sx={{
                                                                    py: 1.5,
                                                                  }}
                                                                />
                                                              )}
                                                            </Mui.TableContainer>
                                                          </Mui.TableCell>
                                                        </Mui.TableRow>
                                                      )}
                                                    </>
                                                  ),
                                                )}
                                              </Mui.TableBody>
                                              <Mui.TableBody>
                                                <Mui.TableRow sx={tableRowSx}>
                                                  <Mui.TableCell>
                                                    <Mui.Typography>
                                                      Total
                                                    </Mui.Typography>
                                                  </Mui.TableCell>
                                                  {expandVisibleHeaders.map(
                                                    (header) => (
                                                      <Mui.TableCell
                                                        key={header.key}
                                                      >
                                                        {header.key ===
                                                        "over_time"
                                                          ? getDetailedTotalValue(
                                                              detailedDetailsByUserId[
                                                                element?.user_id
                                                              ],
                                                              [
                                                                "total_over_time",
                                                                "total_ot_hours",
                                                              ],
                                                              [
                                                                "over_time",
                                                                "overtime",
                                                              ],
                                                            )
                                                          : header.key ===
                                                              "working_hours"
                                                            ? getDetailedTotalValue(
                                                                detailedDetailsByUserId[
                                                                  element?.user_id
                                                                ],
                                                                [
                                                                  "total_working_hours",
                                                                ],
                                                                [
                                                                  "working_hours",
                                                                ],
                                                              )
                                                            : header.key ===
                                                                "leave_credit_hours"
                                                              ? getDetailedTotalValue(
                                                                  detailedDetailsByUserId[
                                                                    element?.user_id
                                                                  ],
                                                                  [
                                                                    "total_leave_credit_hours",
                                                                    "total_leave_credit_hrs",
                                                                  ],
                                                                  [
                                                                    "Leave_credit_hours",
                                                                    "leave_credit_hours",
                                                                  ],
                                                                )
                                                              : null}
                                                      </Mui.TableCell>
                                                    ),
                                                  )}
                                                  <Mui.TableCell key="action"></Mui.TableCell>
                                                </Mui.TableRow>
                                              </Mui.TableBody>
                                            </ApproveTable>
                                          )
                                        ) : (
                                          <EmptyState
                                            size={64}
                                            title="No Shifts Available"
                                            subtitle="There are no available shifts for this employee."
                                            sx={{ py: 2 }}
                                          />
                                        )}
                                      </Mui.TableContainer>
                                    </Mui.TableCell>
                                  </Mui.TableRow>
                                ) : null}
                              </React.Fragment>
                            ))
                          )}
                        </Mui.TableBody>
                      </ApproveTable>
                    </Mui.TableContainer>
                  </Mui.Box>

                  <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
                    {paginationFooter({
                      page,
                      rowsPerPage,
                      total: workingHourData?.length || 0,
                      itemLabel: "records",
                      onPageChange: setLeavePage,
                      onRowsPerPageChange: handleChangeRowsPerPage,
                      rowsPerPageOptions,
                    })}
                  </Mui.Box>
                </Box>
              </Box>
            )}
          </FilterProvider>
        </ThemeProvider>
      ) : loading &&
        reportedType !== RAW_PUNCH_ENTRY_TYPE &&
        reportedType !== WORKING_HOURS_REPORT_TYPE ? (
        <Loader />
      ) : isPunchBasedReport(reportedType) ? (
        <PunchDetails
          key={`${reportedType}:${punchViewMode?.id || "employee-wise"}:${startDate}:${endDate}`}
          masterPunchDetails={masterPunchDetails}
          departmentId={departmentId}
          startDate={startDate}
          endDate={endDate}
          departmentName={departmentName}
          shiftCategoryName={shiftCatagoryName}
          selectedDateRange={selectedDateRange}
          removeExpand={removeExpand}
          setRemoveExpand={setRemoveExpand}
          fetchPunchDetails={fetchPunchDetails}
          setFetchPunchDetails={setFetchPunchDetails}
          setPunchExports={setPunchExports}
          page={page}
          setLeavePage={setLeavePage}
          rowsPerPage={rowsPerPage}
          setLeaveRowsPerPage={setLeaveRowsPerPage}
          punchExports={punchExports}
          punchDetailsData={punchDetailsData}
          showtitle_count={true}
          showFilter={false}
          filters={null}
          hideCount={true}
          metadata={getReportMetadata()}
          mode={
            reportedType === ADJUSTMENT_REPORT_TYPE ? "adjustment" : "punch"
          }
          viewMode={punchViewMode?.id}
          customDownloadHandler={
            reportedType === ADJUSTMENT_REPORT_TYPE
              ? handleAdjustmentReportDownload
              : handlePunchReportDownload
          }
          onVisibleHeadersChange={handlePunchVisibleHeadersChange}
        />
      ) : reportedType === RAW_PUNCH_ENTRY_TYPE ? (
        <RawPunchEntryDetailsReport
          embedded
          setShowReport={setShowReport}
          startDate={startDate}
          endDate={endDate}
          departmentId={departmentId}
        />
      ) : reportedType === WORKING_HOURS_REPORT_TYPE ? (
        <WrkedHrsReports
          setShowReport={setShowReport}
          embeddedReportTypes={reportTypeOptions}
          selectedReportType={reportedType}
          onReportTypeChange={updateReportName}
        />
      ) : reportedType === TIME_CARD_REPORT_TYPE ? (
        <TimeCardReport
          data={workingHoursDetails}
          page={page}
          setLeavePage={setLeavePage}
          rowsPerPage={rowsPerPage}
          setLeaveRowsPerPage={setLeaveRowsPerPage}
          metadata={getReportMetadata()}
          selectedEmployee={selectedEmployee}
          screenAccess={check_access}
          subModuleId={subModuleId}
          startDate={startDate}
          endDate={endDate}
          viewMode={timeCardViewMode}
        />
      ) : (
        <OtReports
          workingHoursDetails={workingHoursDetails}
          exportOTime={exportOTime}
          exportOverTimeReport={exportOverTimeReport}
          departmentName={departmentName}
          shiftCatagoryName={shiftCatagoryName}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      {openSidePeek ? (
        <EditCheckInOut
          setOpenSidePeek={setOpenSidePeek}
          openSidePeek={openSidePeek}
          shiftDetails={shiftDetails}
          setEditState={setEditState}
          departmentId={departmentIdValue}
        />
      ) : null}
    </React.Fragment>
  );
};
